import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { MapPin, Home, Building2, Gavel, Camera, Upload, Mic, CheckCircle, ArrowRight, ArrowLeft, Send, Eye, Award, Shield, Clock, Target, FileText, Image, Video, File, Star, TrendingUp, Navigation, X, Users, Check, Euro, CreditCard, Mail, Phone, BarChart3, Landmark, Briefcase, Leaf, Gift, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import AddressInput from '../components/AddressInput';
import { rememberPartner, clearPartner, withPartner, getStoredPartner } from '../lib/partnerContext';
import { projectsApi } from '../services/api';
import { applyPwaBranding } from '../lib/pwa';
import { InstallPwaButton } from '../components/InstallPwaButton';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Custom marker icon for Leaflet
const customIcon = new L.Icon({
  iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
  iconRetinaUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
  shadowUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Component to handle map clicks for repositioning marker
const LocationMarker = ({ position, onPositionChange }) => {
  useMapEvents({
    click(e) {
      onPositionChange(e.latlng.lat, e.latlng.lng);
    },
  });
  
  return position ? <Marker position={position} icon={customIcon} /> : null;
};

// Video diagnostic types
const VIDEO_TYPES = [
  { id: 'structure', label: 'Sols & Plafonds', description: 'Filmez les angles des plafonds et les sols (fissures, humidité)', icon: Eye },
  { id: 'electricite', label: 'Électricité', description: 'Ouvrez le tableau électrique et filmez les disjoncteurs', icon: Eye },
  { id: 'plomberie', label: 'Plomberie', description: 'Filmez sous l\'évier et les évacuations des WC', icon: Eye },
  { id: 'menuiseries', label: 'Menuiseries', description: 'Ouvrez une fenêtre et filmez l\'épaisseur du vitrage', icon: Eye },
  { id: 'commentaire', label: 'Note Vocale', description: 'Expliquez en 30 secondes votre projet', icon: Mic },
  { id: 'autres_videos', label: 'Autres Vidéos', description: 'Ajoutez autant de vidéos que nécessaire (cave, garage, extérieur...)', icon: Video, multiple: true },
];

// Document types
const DOCUMENT_TYPES = [
  { id: 'dpe', label: 'DPE / Diagnostic Énergétique', description: 'Le diagnostic de performance énergétique du bien', icon: FileText },
  { id: 'taxe_fonciere', label: 'Taxe Foncière', description: 'Avis d\'imposition de la taxe foncière', icon: FileText },
  { id: 'plans', label: 'Plans / Cadastre', description: 'Plans du bien ou relevé cadastral', icon: File },
  { id: 'autres_docs', label: 'Autres Documents', description: 'Ajoutez autant de documents que nécessaire (PV AG, règlement copro, diagnostics...)', icon: File, multiple: true },
];

// Photo types
const PHOTO_TYPES = [
  { id: 'facade', label: 'Façade / Extérieur', description: 'Photo de la façade et de l\'environnement', icon: Image },
  { id: 'pieces', label: 'Pièces Principales', description: 'Salon, chambres, cuisine...', icon: Image },
  { id: 'defauts', label: 'Défauts Visibles', description: 'Fissures, humidité, dégâts...', icon: Image },
  { id: 'autres_photos', label: 'Autres Photos', description: 'Tout élément utile pour l\'analyse', icon: Image },
];

const SALE_TYPES = [
  {
    id: 'particulier',
    sale_type: 'classique',
    fiscal_profile: 'particulier',
    label: 'Particulier',
    rate: '?%',
    hiddenRate: '7-8%',
    description: 'Résidence principale ou achat locatif en direct (personne physique)',
    details: 'Plus-value 19% IR + 17,2% PS · Exonération totale après 22 ans (30 ans pour PS) · Résidence principale exonérée',
    icon: Home,
    color: 'border-blue-500 bg-blue-50',
  },
  {
    id: 'sci_ir',
    sale_type: 'classique',
    fiscal_profile: 'sci_ir',
    label: 'SCI à l\'IR',
    rate: '?%',
    hiddenRate: '7-8%',
    description: 'Société Civile Immobilière — Transparence fiscale',
    details: 'Chaque associé est imposé au barème IR + PS sur sa quote-part · Pas d\'amortissement · Recommandé pour la détention patrimoniale et la transmission familiale',
    icon: Landmark,
    color: 'border-cyan-500 bg-cyan-50',
  },
  {
    id: 'sci_is',
    sale_type: 'classique',
    fiscal_profile: 'sci_is',
    label: 'SCI à l\'IS',
    rate: '?%',
    hiddenRate: '7-8%',
    description: 'Société Civile Immobilière — Impôt sur les Sociétés',
    details: 'IS 15% jusqu\'à 42 500 € puis 25% · Amortissement du bâti possible (baisse IR annuel mais augmente la PV à la revente) · Recommandé pour location meublée longue durée et réinvestissement',
    icon: Briefcase,
    color: 'border-indigo-500 bg-indigo-50',
  },
  {
    id: 'marchand',
    sale_type: 'marchand',
    fiscal_profile: 'marchand_biens',
    label: 'Marchand de Biens',
    rate: '?%',
    hiddenRate: '2.5%',
    description: 'Activité commerciale — Engagement de revente < 5 ans',
    details: 'Frais notaire réduits à 2,5% · TVA sur marge (ou sur prix total) · IS 15%/25% · Obligations : RC Pro, Dommage-Ouvrage, garantie financière',
    icon: Building2,
    color: 'border-amber-500 bg-amber-50',
  },
  {
    id: 'encheres',
    sale_type: 'encheres',
    fiscal_profile: 'particulier',
    label: 'Vente aux Enchères',
    rate: '?%',
    hiddenRate: '20%',
    description: 'Enchères judiciaires ou notariales',
    details: 'Frais poursuivant + émoluments + droits d\'enregistrement ~20% du prix d\'adjudication · Bien acquis en l\'état · Profil fiscal à préciser avec l\'expert',
    icon: Gavel,
    color: 'border-red-500 bg-red-50',
  },
];

// Expert Badge Component
const ExpertBadge = ({ className = "", showNetwork = false }) => (
  <div className={`flex flex-col items-center gap-1 ${className}`}>
    <div className="inline-flex items-center gap-2 px-4 py-2 bg-navy/5 border border-navy/20 rounded-full">
      <Award className="w-4 h-4 text-gold" />
      <span className="text-sm font-medium text-navy">
        Expertise certifiée : <span className="text-gold">25 ans d'expérience bâtiment</span>
      </span>
    </div>
    {showNetwork && (
      <p className="text-xs italic text-navy/60 mt-1">
        Accès exclusif : +50 artisans partenaires & tarifs direct fournisseurs négociés
      </p>
    )}
  </div>
);

// Expert Signature Message Component
const ExpertSignature = ({ compact = false }) => {
  const message = "L'Œil du Marchand n'est pas une simple calculatrice. C'est le fruit de 25 ans d'expérience sur le terrain, entre chantiers complexes et arbitrages immobiliers. Ma mission : transformer votre visite vidéo en un audit technique et financier imprenable. Ne laissez pas un vice caché ou une erreur de chiffrage briser votre rentabilité. Investissez avec l'assurance d'un ancien du bâtiment.";
  
  if (compact) {
    return (
      <div className="bg-gradient-to-r from-navy/5 to-gold/5 border border-navy/10 rounded-xl p-4">
        <p className="text-sm italic text-navy/80 leading-relaxed">
          "{message.slice(0, 150)}..."
        </p>
        <p className="text-xs text-gold font-semibold mt-2">— L'Expert, 25 ans de métier</p>
      </div>
    );
  }
  
  return (
    <div className="bg-gradient-to-br from-navy/5 via-white to-gold/5 border-2 border-navy/10 rounded-2xl p-6 md:p-8 shadow-lg">
      <div className="flex items-start gap-4">
        <div className="hidden md:flex flex-shrink-0 w-16 h-16 rounded-full bg-navy items-center justify-center">
          <Award className="w-8 h-8 text-gold" />
        </div>
        <div className="flex-1">
          <h3 className="font-heading text-lg md:text-xl font-bold text-navy mb-3 flex items-center gap-2">
            <Award className="w-5 h-5 text-gold md:hidden" />
            Le Mot de l'Expert
          </h3>
          <p className="text-navy/80 italic leading-relaxed font-serif text-sm md:text-base">
            "{message}"
          </p>
          <div className="mt-4 flex items-center gap-3">
            <div className="h-px flex-1 bg-gradient-to-r from-gold/50 to-transparent"></div>
            <p className="text-gold font-semibold text-sm">— L'Expert, 25 ans de métier</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function CollectorPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, user: authUser, loading: authLoading, logout } = useAuth();
  const fileInputRefs = useRef({});
  
  // Partner tracking from URL params
  // Résolution du contexte partenaire :
  // 1. URL ?partner= (priorité absolue)
  // 2. Profil utilisateur connecté (client partenaire authentifié)
  // 3. null — les non-connectés seront redirigés via l'effet ci-dessous
  const partnerSubdomain = searchParams.get('partner') || authUser?.partner_subdomain || null;
  const freeAuditToken = searchParams.get('free_token') || null;
  // Mémorise le partenaire (marque blanche) ; la racine "/" sans ?partner= = app principale → on oublie le partenaire
  useEffect(() => {
    if (partnerSubdomain) rememberPartner(partnerSubdomain); else clearPartner();
  }, [partnerSubdomain]);

  // Si un utilisateur non-connecté arrive sur "/" sans ?partner= mais a un partenaire
  // en localStorage (= il venait d'une page partenaire), on le redirige proprement
  // vers /p/{subdomain} pour éviter toute fuite vers l'espace admin principal.
  useEffect(() => {
    if (authLoading) return;
    if (!searchParams.get('partner') && !authUser?.partner_subdomain && !isAuthenticated) {
      const stored = getStoredPartner();
      if (stored) navigate(`/p/${stored}`, { replace: true });
    }
  }, [authLoading, isAuthenticated]); // eslint-disable-line
  
  // Affiliation : capturer et mémoriser le code affilié depuis ?ref=CODE
  const affiliateRefFromUrl = searchParams.get('ref') || '';
  useEffect(() => {
    if (affiliateRefFromUrl) localStorage.setItem('affiliate_ref', affiliateRefFromUrl);
  }, [affiliateRefFromUrl]);
  const affiliateRef = affiliateRefFromUrl || localStorage.getItem('affiliate_ref') || '';
  const initialMode = searchParams.get('mode') || 'estimation';
  
  // Mode: 'estimation' (lead magnet), 'audit' (full tunnel), 'chantier' (VIP), or 'conseil' (Strategic Consulting)
  const [mode, setMode] = useState(initialMode);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPartnerLockedModal, setShowPartnerLockedModal] = useState(false);
  // Partner branding (logo + name + color) — chargé si ?partner=xxx présent dans l'URL
  const [partnerInfo, setPartnerInfo] = useState(null);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [geolocating, setGeolocating] = useState(false);
  const [showMap, setShowMap] = useState(false);
  
  // VIP Sub-mode selection: null (show choice), 'travaux', 'conseil'
  const [vipSubMode, setVipSubMode] = useState(null);
  
  // Estimation mode states
  const [estimationStep, setEstimationStep] = useState(1);
  const [estimationEmail, setEstimationEmail] = useState('');
  const [estimationResult, setEstimationResult] = useState(null);
  const [estimationLoading, setEstimationLoading] = useState(false);
  const [propertyType, setPropertyType] = useState('appartement');

  // === ALGÉRIE — Estimation DZD ===
  const [algeriaZone, setAlgeriaZone] = useState('');
  const [algeriaPropertyType, setAlgeriaPropertyType] = useState('appartement');
  const [algeriaSurface, setAlgeriaSurface] = useState('');
  const [algeriaEmail, setAlgeriaEmail] = useState('');
  const [algeriaPhone, setAlgeriaPhone] = useState('');
  const [algeriaStep, setAlgeriaStep] = useState(1);
  const [algeriaResult, setAlgeriaResult] = useState(null);
  const [algeriaLoading, setAlgeriaLoading] = useState(false);
  const [algeriaPriceGrid, setAlgeriaPriceGrid] = useState(null);

  // === DRAFT / Reprise audit ===
  const DRAFT_KEY = 'audit_draft_v1';
  const [showDraftBanner, setShowDraftBanner] = useState(false);
  const [draftServerToken, setDraftServerToken] = useState(null);
  const [isRedirectingToStripe, setIsRedirectingToStripe] = useState(false);
  const isRedirectingToStripeRef = useRef(false);

  // === PARTNER BRANDING (récupéré depuis /api/partners/by-subdomain/{subdomain}) ===
  useEffect(() => {
    if (!partnerSubdomain) { setPartnerInfo(null); return; }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/partners/by-subdomain/${partnerSubdomain}`);
        if (!cancelled) {
          setPartnerInfo(res.data);
          // Si partenaire algérien, charger la grille tarifaire
          if (res.data?.country === 'algerie') {
            const gridRes = await axios.get(`${API}/algerie/price-grid`);
            if (!cancelled) setAlgeriaPriceGrid(gridRes.data);
          }
        }
      } catch (_e) {
        if (!cancelled) setPartnerInfo(null);
      }
    })();
    return () => { cancelled = true; };
  }, [partnerSubdomain]);

  useEffect(() => { applyPwaBranding(partnerSubdomain, partnerInfo); }, [partnerSubdomain, partnerInfo]);

  // Chantier VIP mode states
  const [chantierData, setChantierData] = useState({
    address: '',
    budget: '',
    phone: '',
    email: '',
    description: ''
  });
  const [chantierSubmitted, setChantierSubmitted] = useState(false);
  const [chantierLoading, setChantierLoading] = useState(false);
  
  // Conseil Stratégique states
  const [conseilData, setConseilData] = useState({
    phone: '',
    email: '',
    subject: '',
    description: ''
  });
  const [conseilSubmitted, setConseilSubmitted] = useState(false);
  const [showConseilSuiviModal, setShowConseilSuiviModal] = useState(false);
  const [conseilSuiviEmail, setConseilSuiviEmail] = useState('');
  const [conseilSuiviCode, setConseilSuiviCode] = useState('');
  const [conseilSuiviLoading, setConseilSuiviLoading] = useState(false);
  const [conseilLoading, setConseilLoading] = useState(false);
  const [conseilFiles, setConseilFiles] = useState([]);
  
  // Budget options for Chantier VIP
  const BUDGET_OPTIONS = [
    { value: 'moins_20k', label: 'Moins de 20 000 €' },
    { value: '20k_50k', label: '20 000 € - 50 000 €' },
    { value: '50k_100k', label: '50 000 € - 100 000 €' },
    { value: 'plus_100k', label: 'Plus de 100 000 €' },
  ];
  
  // Property types for estimation
  const PROPERTY_TYPES = [
    { id: 'appartement', label: 'Appartement', icon: '🏢' },
    { id: 'maison', label: 'Maison / Villa', icon: '🏠' },
    { id: 'immeuble', label: 'Immeuble', icon: '🏛️' },
    { id: 'terrain', label: 'Terrain', icon: '🌳' },
    { id: 'local_commercial', label: 'Local Commercial', icon: '🏪' },
    { id: 'bureau', label: 'Bureau / Locaux pro', icon: '🏢' },
    { id: 'entrepot', label: 'Entrepôt / Activité', icon: '🏭' },
    { id: 'parking', label: 'Parking / Box', icon: '🅿️' },
  ];

  // Sub-categories per property type
  const PROPERTY_SUBCATEGORIES = {
    appartement: [
      { id: 'studio', label: 'Studio / T1' },
      { id: 't2', label: 'T2' },
      { id: 't3', label: 'T3' },
      { id: 't4', label: 'T4' },
      { id: 't5_plus', label: 'T5 et +' },
      { id: 'duplex', label: 'Duplex / Triplex' },
      { id: 'penthouse', label: 'Attique / Dernier étage' },
    ],
    maison: [
      { id: 'plain_pied', label: 'Plain-pied' },
      { id: 'etage', label: 'Maison à étage' },
      { id: 'villa', label: 'Villa' },
      { id: 'longere', label: 'Longère / Fermette' },
      { id: 'contemporaine', label: 'Contemporaine' },
      { id: 'mitoyenne', label: 'Mitoyenne' },
    ],
    immeuble: [
      { id: 'rapport', label: 'Immeuble de rapport' },
      { id: 'mixte', label: 'Mixte (habitation + commerce)' },
      { id: 'copropriete', label: 'Copropriété' },
    ],
    terrain: [
      { id: 'constructible', label: 'Constructible' },
      { id: 'agricole', label: 'Agricole' },
      { id: 'loisir', label: 'Terrain de loisir' },
    ],
    local_commercial: [
      { id: 'boutique', label: 'Boutique / Commerce' },
      { id: 'restaurant', label: 'Restaurant / Bar' },
      { id: 'bureau_local', label: 'Bureau' },
    ],
    bureau: [
      { id: 'open_space', label: 'Open Space' },
      { id: 'cloisonne', label: 'Cloisonné' },
      { id: 'coworking', label: 'Coworking' },
    ],
    entrepot: [
      { id: 'stockage', label: 'Stockage' },
      { id: 'activite', label: 'Local d\'activité' },
      { id: 'atelier', label: 'Atelier' },
    ],
    parking: [
      { id: 'souterrain', label: 'Souterrain' },
      { id: 'exterieur', label: 'Extérieur' },
      { id: 'box_ferme', label: 'Box fermé' },
    ],
  };

  // Property features with price impact coefficients
  // Default fallback (used if API fetch fails). Kept in sync with backend DEFAULT_ESTIMATION_COEFFICIENTS.
  const VUE_OPTIONS_DATA = [
    { id: 'mer', label: 'Vue Mer', icon: '🌊', subtitle: 'PACA, Bretagne, Normandie, Atlantique',
      sub: [{ id: 'mer_apercu', label: 'Aperçu / Latérale', coef: 10 }, { id: 'mer_directe', label: 'Dégagée / Directe', coef: 20 }, { id: 'mer_panoramique', label: 'Panoramique / Exceptionnelle', coef: 35 }] },
    { id: 'lac', label: 'Vue Lac', icon: '💧', subtitle: 'Annecy, Léman, Alpes, Grands Lacs',
      sub: [{ id: 'lac_apercu', label: 'Aperçu / Proximité lac', coef: 5 }, { id: 'lac_directe', label: 'Belle vue directe', coef: 15 }, { id: 'lac_panoramique', label: 'Vue panoramique', coef: 30 }] },
    { id: 'montagne', label: 'Vue Montagne / Nature', icon: '🏔️', subtitle: 'Alpes, Pyrénées, Massif Central',
      sub: [{ id: 'montagne_vallee', label: 'Dégagée / Vallée / Campagne', coef: 5 }, { id: 'montagne_sommet', label: 'Imprenable / Sommets / Ski', coef: 15 }] },
    { id: 'degage', label: 'Vue Dégagée', icon: '🏙️', subtitle: 'Urbain — sans vis-à-vis',
      sub: [{ id: 'degage_parc', label: 'Dernier étage / Vue parc', coef: 5 }, { id: 'degage_standard', label: 'Standard (vis-à-vis classique)', coef: 0 }] },
  ];
  const DEFAULT_PROPERTY_FEATURES = {
    appartement: [
      { id: 'balcon', label: 'Balcon', coef: 3, icon: '🌿', inputType: 'surface' },
      { id: 'terrasse', label: 'Terrasse', coef: 5, icon: '☀️' },
      { id: 'parking', label: 'Parking', coef: 5, icon: '🅿️', inputType: 'quantity' },
      { id: 'garage', label: 'Garage', coef: 5, icon: '🚗', inputType: 'quantity' },
      { id: 'cave', label: 'Cave', coef: 2, icon: '🔑', inputType: 'quantity' },
      { id: 'ascenseur', label: 'Ascenseur', coef: 3, icon: '🛗' },
      { id: 'vue_type', label: 'Type de Vue', icon: '🏔️', inputType: 'vue_type' },
      { id: 'dernier_etage', label: 'Dernier étage', coef: 7, icon: '🔝' },
      { id: 'rdc', label: 'Rez-de-chaussée', coef: -5, icon: '⬇️' },
      { id: 'renove', label: 'Rénové / Neuf', coef: 10, icon: '✨' },
      { id: 'a_renover', label: 'À rénover', coef: -15, icon: '🔧' },
      { id: 'lumineux', label: 'Lumineux / Double expo.', coef: 4, icon: '💡' },
      { id: 'gardien', label: 'Gardien / Digicode', coef: 2, icon: '🔐' },
    ],
    maison: [
      { id: 'piscine', label: 'Piscine', coef: 8, icon: '🏊' },
      { id: 'jardin', label: 'Jardin', coef: 5, icon: '🌳' },
      { id: 'garage', label: 'Garage', coef: 4, icon: '🚗', inputType: 'quantity' },
      { id: 'parking', label: 'Parking', coef: 3, icon: '🅿️', inputType: 'quantity' },
      { id: 'cave', label: 'Cave', coef: 2, icon: '🔑', inputType: 'quantity' },
      { id: 'terrasse', label: 'Terrasse', coef: 4, icon: '☀️' },
      { id: 'balcon', label: 'Balcon', coef: 3, icon: '🌿', inputType: 'surface' },
      { id: 'sous_sol', label: 'Sous-sol aménagé', coef: 5, icon: '🏠' },
      { id: 'dependance', label: 'Dépendance / Annexe', coef: 6, icon: '🏡' },
      { id: 'vue_type', label: 'Type de Vue', icon: '🏔️', inputType: 'vue_type' },
      { id: 'renove', label: 'Rénové / Neuf', coef: 10, icon: '✨' },
      { id: 'a_renover', label: 'À rénover', coef: -15, icon: '🔧' },
      { id: 'calme', label: 'Quartier calme', coef: 3, icon: '🤫' },
      { id: 'panneau_solaire', label: 'Panneaux solaires', coef: 4, icon: '⚡' },
      { id: 'isolation', label: 'Bonne isolation (DPE A-C)', coef: 5, icon: '🌡️' },
    ],
    immeuble: [
      { id: 'commerce_rdc', label: 'Commerce au RDC', coef: 8, icon: '🏪' },
      { id: 'parking', label: 'Parking privatif', coef: 5, icon: '🚗' },
      { id: 'renove', label: 'Rénové / Bon état', coef: 10, icon: '✨' },
      { id: 'a_renover', label: 'À rénover', coef: -15, icon: '🔧' },
      { id: 'toiture_neuve', label: 'Toiture neuve', coef: 5, icon: '🏗️' },
      { id: 'plein_centre', label: 'Centre-ville', coef: 8, icon: '📍' },
      { id: 'tous_loues', label: 'Tous les lots loués', coef: 5, icon: '📋' },
    ],
    terrain: [
      { id: 'viabilise', label: 'Viabilisé', coef: 15, icon: '⚡' },
      { id: 'plat', label: 'Terrain plat', coef: 5, icon: '📐' },
      { id: 'vue_type', label: 'Type de Vue', icon: '🏔️', inputType: 'vue_type' },
      { id: 'acces_route', label: 'Accès route', coef: 5, icon: '🛣️' },
      { id: 'cu_positif', label: 'CU positif', coef: 10, icon: '📋' },
    ],
    local_commercial: [
      { id: 'vitrine', label: 'Vitrine sur rue', coef: 10, icon: '🪟' },
      { id: 'emplacement', label: 'Emplacement N°1', coef: 15, icon: '📍' },
      { id: 'extraction', label: 'Extraction / Hotte', coef: 5, icon: '💨' },
      { id: 'reserve', label: 'Réserve / Stockage', coef: 3, icon: '📦' },
      { id: 'renove', label: 'Rénové', coef: 8, icon: '✨' },
    ],
    bureau: [
      { id: 'climatisation', label: 'Climatisation', coef: 5, icon: '❄️' },
      { id: 'fibre', label: 'Fibre optique', coef: 3, icon: '🌐' },
      { id: 'parking', label: 'Parking', coef: 5, icon: '🚗' },
      { id: 'acces_handicape', label: 'Accès handicapé', coef: 3, icon: '♿' },
    ],
    entrepot: [
      { id: 'quai', label: 'Quai de déchargement', coef: 8, icon: '🚛' },
      { id: 'hauteur', label: 'Grande hauteur', coef: 5, icon: '📏' },
      { id: 'acces_pl', label: 'Accès poids lourd', coef: 5, icon: '🚚' },
    ],
    parking: [
      { id: 'securise', label: 'Sécurisé / Fermé', coef: 10, icon: '🔒' },
      { id: 'borne_elec', label: 'Borne électrique', coef: 8, icon: '⚡' },
      { id: 'proche_centre', label: 'Proche centre-ville', coef: 10, icon: '📍' },
    ],
  };
  
  // Dynamic coefficients fetched from admin-configurable backend (with fallback to defaults)
  const [PROPERTY_FEATURES, setPropertyFeatures] = useState(DEFAULT_PROPERTY_FEATURES);
  
  useEffect(() => {
    axios.get(`${API}/estimation-coefficients`)
      .then(res => {
        if (res.data && Object.keys(res.data).length > 0) {
          // Remplacer 'vue' par 'vue_type' pour la sélection dynamique de vue
          const transformed = {};
          for (const [propType, features] of Object.entries(res.data)) {
            transformed[propType] = features.map(f =>
              f.id === 'vue' ? { id: 'vue_type', label: 'Type de Vue', icon: '🏔️', inputType: 'vue_type' } : f
            );
          }
          setPropertyFeatures(transformed);
        }
      })
      .catch(() => {/* keep defaults */});
  }, []);

  const [propertySubType, setPropertySubType] = useState('');
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [featureQuantities, setFeatureQuantities] = useState({}); // { parking: 2, garage: 1, cave: 1, balcon_m2: 12 }
  const [vueCategory, setVueCategory] = useState(null);
  const [vueSub, setVueSub] = useState(null);
  const [vueExpanded, setVueExpanded] = useState(false);
  const [vueOptionsData, setVueOptionsData] = useState(() => VUE_OPTIONS_DATA.map(cat => ({
    id: cat.id, label: cat.label, icon: cat.icon, subtitle: cat.subtitle,
    sub: (cat.sub || []).map(s => ({ id: s.id, label: s.label, coef: s.coef }))
  })));

  useEffect(() => {
    axios.get(`${API}/public/vue-coefficients`)
      .then(res => {
        if (res.data && res.data.length) {
          const normalized = res.data.map(cat => ({
            id: cat.category_id || cat.id,
            label: cat.category_label || cat.label,
            icon: cat.category_icon || cat.icon,
            subtitle: cat.category_subtitle || cat.subtitle,
            sub: (cat.subs || cat.sub || []).map(s => ({ id: s.sub_id || s.id, label: s.sub_label || s.label, coef: s.coef }))
          }));
          setVueOptionsData(normalized);
        }
      })
      .catch(() => {});
  }, []);

  const toggleFeature = (featureId) => {
    setSelectedFeatures(prev => {
      if (prev.includes(featureId)) {
        // Remove feature and its quantity
        setFeatureQuantities(q => { const nq = {...q}; delete nq[featureId]; return nq; });
        return prev.filter(f => f !== featureId);
      }
      return [...prev, featureId];
    });
  };

  const QUANTIFIABLE_FEATURES = ['parking', 'garage', 'cave'];
  const SURFACE_FEATURES = ['balcon'];

  const updateFeatureQuantity = (featureId, value) => {
    setFeatureQuantities(prev => ({ ...prev, [featureId]: Math.max(0, value) }));
  };

  const getTotalCoefficient = () => {
    const features = PROPERTY_FEATURES[propertyType] || [];
    const base = selectedFeatures.reduce((total, fId) => {
      const feature = features.find(f => f.id === fId);
      if (!feature) return total;
      const qty = featureQuantities[fId] || 1;
      if (QUANTIFIABLE_FEATURES.includes(fId)) {
        return total + (feature.coef * qty);
      }
      if (SURFACE_FEATURES.includes(fId)) {
        const surfaceM2 = featureQuantities[fId] || 0;
        return total + (surfaceM2 > 0 ? feature.coef + Math.floor(surfaceM2 / 5) : feature.coef);
      }
      return total + (feature.coef || 0);
    }, 0);
    return base + (vueSub?.coef || 0);
  };
  
  const [formData, setFormData] = useState({
    address: '',
    address_precision: '',  // Lot N° / Bât / Étage / Pavillon (utile pour lotissements et résidences)
    latitude: null,
    longitude: null,
    profile_type: 'particulier',
    sale_type: 'classique',
    fiscal_profile: 'particulier',
    sale_type_id: 'particulier',  // UI selector id (particulier | sci_ir | sci_is | marchand | encheres)
    surface: '',
    land_surface: '',
    purchase_price: '',
    estimated_resale_price: '',
    // Audit fiscal optimisé (facultatif)
    purchase_year: '',                    // Année d'acquisition (pour abattements PV)
    historical_purchase_price: '',        // Prix d'achat HISTORIQUE si bien déjà possédé
    construction_year: '',                // Année de construction (DPE/MPR/SRU)
    dpe_class: '',                        // Classe DPE (A-G) - optionnel pour alertes réglementaires
    is_residence_principale: false,       // Résidence principale ? (exonération PV)
    phone: '',
    videos: {},
    documents: {},
    photos: {}
  });

  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [partnerSubmitSuccess, setPartnerSubmitSuccess] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [activeMediaTab, setActiveMediaTab] = useState('videos');
  const [selectedPackage, setSelectedPackage] = useState('pro'); // Default to Pro

  const totalSteps = 5;
  const totalEstimationSteps = 4;
  const progress = mode === 'audit' ? (step / totalSteps) * 100 : (estimationStep / totalEstimationSteps) * 100;

  // === DRAFT MANAGEMENT (localStorage + beforeunload + URL token) ===

  // 1. On mount : check URL for ?draft_token=xxx then check localStorage
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const urlToken = params.get('draft_token');
    if (urlToken) {
      axios.get(`${API}/audit/draft/${urlToken}`)
        .then(res => {
          const fd = res.data?.form_data || {};
          if (fd.formData) setFormData(prev => ({ ...prev, ...fd.formData }));
          if (fd.propertyType) setPropertyType(fd.propertyType);
          if (fd.propertySubType) setPropertySubType(fd.propertySubType);
          if (Array.isArray(fd.selectedFeatures)) setSelectedFeatures(fd.selectedFeatures);
          if (fd.featureQuantities) setFeatureQuantities(fd.featureQuantities);
          if (typeof fd.step === 'number') setStep(fd.step);
          if (fd.mode) setMode(fd.mode);
          setDraftServerToken(urlToken);
          toast.success('Audit en cours restauré ✓');
        })
        .catch(() => toast.error('Lien de reprise expiré ou invalide'));
      return;
    }
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        const ageHours = (Date.now() - (parsed.savedAt || 0)) / 1000 / 3600;
        // Ne proposer la reprise que si le brouillon vient bien du tunnel Audit
        // (évite qu'un brouillon d'Estimation renvoie l'utilisateur hors du tunnel Audit)
        if (ageHours < 24 * 14 && parsed?.mode === 'audit' && (parsed?.formData?.address || parsed?.formData?.purchase_price)) {
          setShowDraftBanner(true);
        } else if (ageHours >= 24 * 14) {
          localStorage.removeItem(DRAFT_KEY);
        }
      }
    } catch { /* ignore */ }
  }, []); // eslint-disable-line

  // 2. Auto-save dans localStorage (debounced)
  useEffect(() => {
    const t = setTimeout(() => {
      try {
        // Exclure videos/documents/photos : les objets File ne sont pas sérialisables en JSON
        // (deviennent {} vides puis polluent le state à la restauration → fichiers perdus).
        const { videos: _v, documents: _d, photos: _p, ...formDataWithoutFiles } = formData;
        const snapshot = {
          formData: formDataWithoutFiles, propertyType, propertySubType, selectedFeatures, featureQuantities, step, mode,
          savedAt: Date.now(),
        };
        if (formData.address || formData.purchase_price || formData.email) {
          localStorage.setItem(DRAFT_KEY, JSON.stringify(snapshot));
        }
      } catch { /* quota → ignore */ }
    }, 500);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [formData, propertyType, propertySubType, selectedFeatures, featureQuantities, step, mode]);

  // 3. Avertir si fermeture/refresh avec données non sauvegardées
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      // Ref checked synchronously: si le paiement est en cours (upload/register/checkout), ne pas bloquer
      if (isRedirectingToStripeRef.current || isRedirectingToStripe) return;
      if (mode === 'audit' && (formData.address || formData.purchase_price)) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
    // eslint-disable-next-line
  }, [mode, formData.address, formData.purchase_price, isRedirectingToStripe]);

  const restoreDraft = () => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed.formData) {
        // Ne PAS écraser videos/documents/photos avec le brouillon (peuvent contenir des Files
        // encore en mémoire dans la session courante). Le brouillon n'a jamais les fichiers.
        const { videos: _v, documents: _d, photos: _p, ...draftWithoutFiles } = parsed.formData;
        setFormData(prev => ({ ...prev, ...draftWithoutFiles }));
      }
      if (parsed.propertyType) setPropertyType(parsed.propertyType);
      if (parsed.propertySubType) setPropertySubType(parsed.propertySubType);
      if (Array.isArray(parsed.selectedFeatures)) setSelectedFeatures(parsed.selectedFeatures);
      if (parsed.featureQuantities) setFeatureQuantities(parsed.featureQuantities);
      if (typeof parsed.step === 'number') setStep(parsed.step);
      if (parsed.mode) setMode(parsed.mode);
      setShowDraftBanner(false);
      toast.success('Audit en cours restauré ✓');
      // Avertir que les fichiers ne sont pas sauvegardés dans le brouillon
      toast.info('⚠️ Pensez à réajouter vos photos, vidéos et documents (non conservés dans le brouillon)', { duration: 8000 });
    } catch { toast.error('Impossible de restaurer'); }
  };

  const discardDraft = () => {
    localStorage.removeItem(DRAFT_KEY);
    setShowDraftBanner(false);
  };

  const saveDraftOnServer = async () => {
    if (!formData.email) {
      toast.error('Renseignez votre email pour activer la sauvegarde sur le serveur');
      return;
    }
    try {
      const snapshot = { formData, propertyType, propertySubType, selectedFeatures, featureQuantities, step, mode };
      const res = await axios.post(`${API}/audit/draft/save`, {
        email: formData.email, form_data: snapshot, send_recovery_email: true,
      });
      setDraftServerToken(res.data.token);
      toast.success('Sauvegardé ✓ Vous avez reçu un email pour reprendre depuis n\'importe quel appareil.');
    } catch {
      toast.error('Erreur lors de la sauvegarde serveur');
    }
  };


  // Estimation Algérie DZD
  const getAlgeriaEstimation = async () => {
    if (!algeriaEmail || !algeriaZone || !algeriaPropertyType || !algeriaSurface) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    setAlgeriaLoading(true);
    try {
      const response = await axios.post(`${API}/algerie/estimate`, {
        zone: algeriaZone,
        property_type: algeriaPropertyType,
        surface: parseFloat(algeriaSurface),
        email: algeriaEmail,
        phone: algeriaPhone || undefined,
        features_coefficient: getTotalCoefficient(),
        partner_subdomain: partnerSubdomain || undefined,
      });
      setAlgeriaResult(response.data);
      setAlgeriaStep(4);
      toast.success('Estimation envoyée par email !');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'estimation');
    } finally {
      setAlgeriaLoading(false);
    }
  };


  // Estimation DVF - Get price and save prospect
  const getEstimation = async () => {
    if (!estimationEmail || !formData.address || !formData.surface) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }
    
    setEstimationLoading(true);
    try {
      const response = await axios.post(`${API}/estimation/dvf`, {
        email: estimationEmail,
        phone: formData.phone || undefined,
        address: formData.address,
        surface: parseFloat(formData.surface),
        land_surface: formData.land_surface ? parseFloat(formData.land_surface) : null,
        property_type: propertyType,
        property_sub_type: propertySubType || undefined,
        features: selectedFeatures,
        feature_quantities: featureQuantities,
        features_coefficient: getTotalCoefficient(),
        ...(partnerSubdomain && { partner_subdomain: partnerSubdomain })
      });
      
      setEstimationResult(response.data);
      toast.success('Estimation envoyée par email ! Vérifiez vos spams si besoin.');
    } catch (error) {
      console.error('Estimation error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'estimation');
    } finally {
      setEstimationLoading(false);
    }
  };

  // Switch mode and reset steps
  const switchMode = (newMode) => {
    setMode(newMode);
    if (newMode === 'estimation') {
      setEstimationStep(1);
      setEstimationResult(null);
      setEstimationEmail('');
    } else if (newMode === 'chantier') {
      setVipSubMode(null); // Reset to show choice screen
      setChantierSubmitted(false);
      setChantierData({ address: '', budget: '', phone: '', email: '', description: '' });
      setConseilSubmitted(false);
      setConseilData({ phone: '', email: '', subject: '', description: '' });
    } else {
      setStep(1);
    }
  };

  // Submit Conseil Stratégique request
  const submitConseilRequest = async () => {
    if (!conseilData.phone || !conseilData.email || !conseilData.subject) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setConseilLoading(true);
    try {
      await axios.post(`${API}/conseil/request`, {
        ...conseilData,
        type: 'conseil_strategique',
        ...(partnerSubdomain && { partner_subdomain: partnerSubdomain })
      });
      setConseilSubmitted(true);
      toast.success('Demande envoyée ! Vous serez contacté sous 24h.');
    } catch (error) {
      console.error('Conseil request error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setConseilLoading(false);
    }
  };

  // Submit Chantier VIP request
  const submitChantierRequest = async () => {
    if (!chantierData.address || !chantierData.budget || !chantierData.email) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setChantierLoading(true);
    try {
      await axios.post(`${API}/chantier/request`, {
        ...chantierData,
        ...(partnerSubdomain && { partner_subdomain: partnerSubdomain })
      });
      setChantierSubmitted(true);
      toast.success('Demande envoyée ! Vous serez rappelé sous 24h.');
    } catch (error) {
      console.error('Chantier request error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setChantierLoading(false);
    }
  };
  
  // Packages for subscription step
  const PACKAGES = [
    {
      id: 'flash',
      name: 'Flash',
      subtitle: 'À l\'unité',
      price: 97,
      priceLabel: '€ TTC',
      credits: 1,
      description: 'Idéal pour tester un premier projet',
      features: [
        '1 audit complet',
        'Rapport PDF "Bank-Ready"',
        'Données DVF du quartier',
        'Support email sous 48h'
      ],
      color: 'border-gray-300 bg-gray-50',
      selectedColor: 'border-gray-500 bg-gray-100 ring-2 ring-gray-500'
    },
    {
      id: 'pro',
      name: 'Marchand Pro',
      subtitle: 'Le plus populaire',
      price: 247,
      priceLabel: '€ TTC',
      credits: 4,
      description: 'Pour les marchands de biens actifs',
      features: [
        '4 audits complets',
        'Analyse prioritaire en 12h',
        'Accès DVF complet',
        'Support prioritaire'
      ],
      popular: true,
      color: 'border-gold/50 bg-gold/5',
      selectedColor: 'border-gold bg-gold/10 ring-2 ring-gold',
      savings: 'Économisez 40%'
    },
    {
      id: 'master',
      name: 'Master VIP',
      subtitle: 'Accompagnement Premium',
      price: 497,
      priceLabel: '€ TTC',
      credits: 10,
      description: 'Suivi personnalisé avec l\'expert',
      features: [
        '10 audits complets',
        '1 session visio 30min',
        'Chat direct avec l\'expert',
        'Ligne directe WhatsApp'
      ],
      color: 'border-navy/30 bg-navy/5',
      selectedColor: 'border-navy bg-navy/10 ring-2 ring-navy',
      badge: 'VIP'
    }
  ];

  // Reverse geocoding - Get address from coordinates
  const reverseGeocode = async (lat, lng) => {
    try {
      const response = await axios.get(
        `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`,
        { headers: { 'Accept-Language': 'fr' } }
      );
      if (response.data && response.data.display_name) {
        return response.data.display_name;
      }
      return null;
    } catch (error) {
      console.error('Reverse geocoding error:', error);
      return null;
    }
  };

  // Update position from map click
  const handleMapPositionChange = async (lat, lng) => {
    const address = await reverseGeocode(lat, lng);
    setFormData(prev => ({
      ...prev,
      latitude: lat,
      longitude: lng,
      address: address || prev.address
    }));
    if (address) {
      toast.success('Position mise à jour !');
    }
  };

  // Geolocation
  const handleGeolocation = async () => {
    if (!navigator.geolocation) {
      toast.error('Géolocalisation non supportée');
      return;
    }
    
    setGeolocating(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        
        // Get address from coordinates via reverse geocoding
        const address = await reverseGeocode(lat, lng);
        
        setFormData(prev => ({
          ...prev,
          latitude: lat,
          longitude: lng,
          address: address || `${lat.toFixed(6)}, ${lng.toFixed(6)}`
        }));
        
        setShowMap(true);
        toast.success('Position récupérée ! Cliquez sur la carte pour ajuster si nécessaire.');
        setGeolocating(false);
      },
      (error) => {
        toast.error('Impossible de récupérer la position');
        setGeolocating(false);
      }
    );
  };

  // Address autocomplete with debounce
  const addressDebounceRef = useRef(null);
  const handleAddressChange = useCallback((value) => {
    setFormData(prev => ({ ...prev, address: value }));
    if (addressDebounceRef.current) clearTimeout(addressDebounceRef.current);
    if (value.length >= 3) {
      addressDebounceRef.current = setTimeout(async () => {
        try {
          const response = await addressApi.autocomplete(value);
          setAddressSuggestions(response.data);
          setShowSuggestions(true);
        } catch (error) {
          console.error('Address autocomplete error:', error);
        }
      }, 350);
    } else {
      setShowSuggestions(false);
    }
  }, []);

  const selectAddress = async (suggestion) => {
    setFormData({
      ...formData,
      address: suggestion.description,
      latitude: null,
      longitude: null
    });
    setShowSuggestions(false);

    try {
      const details = await addressApi.getDetails(suggestion.place_id);
      setFormData(prev => ({
        ...prev,
        latitude: details.data.geometry.latitude,
        longitude: details.data.geometry.longitude
      }));
      // Ouvrir automatiquement la mini-carte pour que le client visualise et puisse ajuster le pin
      setShowMap(true);
    } catch (error) {
      console.error('Address details error:', error);
    }
  };

  // Video upload handlers
  const handleVideoSelect = (videoType, files, isMultiple = false) => {
    if (files && files.length > 0) {
      if (isMultiple) {
        // For "autres_videos" - allow multiple
        const existingVideos = formData.videos[videoType] || [];
        const newVideos = Array.isArray(existingVideos) 
          ? [...existingVideos, ...Array.from(files)]
          : [existingVideos, ...Array.from(files)];
        setFormData(prev => ({
          ...prev,
          videos: {
            ...prev.videos,
            [videoType]: newVideos
          }
        }));
        toast.success(`${files.length} vidéo(s) ajoutée(s)`);
      } else {
        // Single file
        setFormData(prev => ({
          ...prev,
          videos: {
            ...prev.videos,
            [videoType]: files[0]
          }
        }));
        toast.success(`${VIDEO_TYPES.find(v => v.id === videoType)?.label} ajouté`);
      }
    }
  };

  // Document upload handlers
  const handleDocumentSelect = (docType, files, isMultiple = false) => {
    if (files && files.length > 0) {
      if (isMultiple) {
        // For "autres_docs" - allow multiple
        const existingDocs = formData.documents[docType] || [];
        const newDocs = Array.isArray(existingDocs) 
          ? [...existingDocs, ...Array.from(files)]
          : [existingDocs, ...Array.from(files)];
        setFormData(prev => ({
          ...prev,
          documents: {
            ...prev.documents,
            [docType]: newDocs
          }
        }));
        toast.success(`${files.length} document(s) ajouté(s)`);
      } else {
        // Single file
        setFormData(prev => ({
          ...prev,
          documents: {
            ...prev.documents,
            [docType]: files[0]
          }
        }));
        toast.success(`${DOCUMENT_TYPES.find(d => d.id === docType)?.label} ajouté`);
      }
    }
  };

  // Remove video from multiple list
  const removeVideo = (videoType, index) => {
    const videos = formData.videos[videoType];
    if (Array.isArray(videos)) {
      const newVideos = videos.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        videos: {
          ...prev.videos,
          [videoType]: newVideos.length > 0 ? newVideos : undefined
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        videos: {
          ...prev.videos,
          [videoType]: undefined
        }
      }));
    }
    toast.success('Vidéo supprimée');
  };

  // Remove document from multiple list
  const removeDocument = (docType, index) => {
    const docs = formData.documents[docType];
    if (Array.isArray(docs)) {
      const newDocs = docs.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        documents: {
          ...prev.documents,
          [docType]: newDocs.length > 0 ? newDocs : undefined
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        documents: {
          ...prev.documents,
          [docType]: undefined
        }
      }));
    }
    toast.success('Document supprimé');
  };

  // Photo upload handlers
  const handlePhotoSelect = (photoType, files) => {
    if (files && files.length > 0) {
      const existingPhotos = formData.photos[photoType] || [];
      const newPhotos = [...existingPhotos, ...Array.from(files)];
      setFormData(prev => ({
        ...prev,
        photos: {
          ...prev.photos,
          [photoType]: newPhotos
        }
      }));
      toast.success(`${files.length} photo(s) ajoutée(s)`);
    }
  };

  const triggerFileInput = (inputId) => {
    if (fileInputRefs.current[inputId]) {
      fileInputRefs.current[inputId].click();
    }
  };

  const removePhoto = (photoType, index) => {
    setFormData(prev => ({
      ...prev,
      photos: {
        ...prev.photos,
        [photoType]: prev.photos[photoType].filter((_, i) => i !== index)
      }
    }));
  };

  // Submit project - Direct to payment
  const handleSubmit = async () => {
    // Flux audit gratuit (lien admin) : bypass Stripe
    if (freeAuditToken) {
      await handleFreeAuditSubmit();
      return;
    }
    // Flux partenaire : bypass Stripe
    if (partnerSubdomain) {
      if (!isAuthenticated) {
        await handlePartnerDirectSubmit();
      } else {
        await createPartnerProject();
      }
      return;
    }
    // Flux classique (Stripe)
    if (!isAuthenticated) {
      setShowEmailModal(true);
      return;
    }
    
    await createProject();
  };

  // Submit via lien d'audit gratuit admin (bypass Stripe, token usage unique)
  const handleFreeAuditSubmit = async () => {
    if (!isAuthenticated) {
      setShowEmailModal(true);
      return;
    }
    setLoading(true);
    try {
      toast.info('Création de votre dossier...');
      const projectData = {
        address: formData.address,
        address_precision: formData.address_precision || null,
        latitude: formData.latitude,
        longitude: formData.longitude,
        surface: parseFloat(formData.surface) || 0,
        land_surface: formData.land_surface ? parseFloat(formData.land_surface) : null,
        purchase_price: parseFloat(formData.purchase_price) || 0,
        estimated_resale_price: formData.estimated_resale_price ? parseFloat(formData.estimated_resale_price) : null,
        profile_type: formData.profile_type,
        sale_type: formData.sale_type,
        fiscal_profile: formData.fiscal_profile,
        property_type: propertyType,
        property_sub_type: propertySubType || null,
        features: selectedFeatures,
        feature_quantities: featureQuantities,
        features_coefficient: getTotalCoefficient(),
        selected_package: selectedPackage || 'audit_master',
        description: formData.description || '',
      };
      const projectResp = await projectsApi.create(projectData);
      const projectId = projectResp.data.id;

      // Upload des fichiers
      for (const [videoType, fileData] of Object.entries(formData.videos)) {
        if (!fileData) continue;
        if (Array.isArray(fileData)) {
          for (let i = 0; i < fileData.length; i++) { toast.info(`Upload vidéo ${i+1}/${fileData.length}...`); await projectsApi.uploadVideo(projectId, `${videoType}_${i}`, fileData[i]); }
        } else { await projectsApi.uploadVideo(projectId, videoType, fileData); }
      }
      for (const [docType, fileData] of Object.entries(formData.documents)) {
        if (!fileData) continue;
        if (Array.isArray(fileData)) {
          for (let i = 0; i < fileData.length; i++) { await projectsApi.uploadVideo(projectId, `doc_${docType}_${i}`, fileData[i]); }
        } else { await projectsApi.uploadVideo(projectId, `doc_${docType}`, fileData); }
      }
      for (const [photoType, files] of Object.entries(formData.photos)) {
        if (!files || files.length === 0) continue;
        for (let i = 0; i < files.length; i++) { await projectsApi.uploadVideo(projectId, `photo_${photoType}_${i}`, files[i]); }
      }

      // Soumission gratuite (bypass Stripe)
      await axios.post(`${API}/projects/${projectId}/free-audit-submit?token=${freeAuditToken}`);
      try { localStorage.removeItem(DRAFT_KEY); } catch {}
      if (draftServerToken) { axios.delete(`${API}/audit/draft/${draftServerToken}`).catch(() => {}); }
      toast.success('Votre dossier a été transmis ! Vous allez recevoir un email de confirmation.');
      navigate('/dashboard');
    } catch (error) {
      console.error('Free audit submit error:', error);
      toast.error(error.response?.data?.detail || 'Lien invalide ou expiré. Contactez l\'administrateur.');
      setLoading(false);
    }
  };

  // Direct payment flow - email + password then Stripe
  const handleDirectPayment = async () => {
    if (!email || !email.includes('@')) {
      toast.error('Veuillez entrer un email valide');
      return;
    }
    
    if (!phone || phone.trim().length < 8) {
      toast.error('Veuillez entrer un numéro de téléphone valide');
      return;
    }
    
    if (!password || password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    if (password !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    setLoading(true);
    // Bloque immédiatement le "beforeunload" pour éviter l'alerte "Quitter le site?"
    // pendant l'upload de fichiers + register + checkout (avant même la redirection Stripe).
    isRedirectingToStripeRef.current = true;
    try {
      // Get package info
      const packageInfo = PACKAGES.find(p => p.id === selectedPackage);
      const amount = packageInfo?.price || 97;
      const packageName = packageInfo?.name || 'Flash';

      // ============================================================
      // STEP 1 — Register or login the client FIRST (so we have a token
      // to create the project + upload the files BEFORE Stripe redirect).
      // Without this step, files are lost because no project_id exists.
      // ============================================================
      let authToken = null;
      try {
        const regRes = await axios.post(`${API}/auth/client/register`, {
          email,
          password,
          name: formData.email_name || '',
          phone: phone || formData.phone || null,
          ...(partnerSubdomain && { partner_subdomain: partnerSubdomain }),
        });
        authToken = regRes.data.access_token;
      } catch (regErr) {
        // Email exists → try login with the provided password
        if (regErr.response?.status === 400) {
          try {
            const loginRes = await axios.post(`${API}/auth/client/login`, { email, password });
            authToken = loginRes.data.access_token;
          } catch (loginErr) {
            toast.error("Cet email existe déjà avec un mot de passe différent. Utilisez 'Mot de passe oublié' ou changez d'email.");
            setLoading(false);
            return;
          }
        } else {
          throw regErr;
        }
      }
      if (!authToken) {
        toast.error("Impossible d'initialiser votre compte. Réessayez.");
        setLoading(false);
        return;
      }
      // Persist token so the whole session (uploads + return from Stripe) is authenticated
      try { localStorage.setItem('token', authToken); } catch {}
      axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;

      // ============================================================
      // STEP 2 — Create the project via the authenticated endpoint
      // ============================================================
      toast.info('Création de votre dossier...');
      const projectData = {
        address: formData.address,
        address_precision: formData.address_precision || null,
        latitude: formData.latitude,
        longitude: formData.longitude,
        surface: parseFloat(formData.surface) || 0,
        land_surface: formData.land_surface ? parseFloat(formData.land_surface) : null,
        purchase_price: parseFloat(formData.purchase_price) || 0,
        estimated_resale_price: formData.estimated_resale_price ? parseFloat(formData.estimated_resale_price) : null,
        profile_type: formData.profile_type,
        sale_type: formData.sale_type,
        fiscal_profile: formData.fiscal_profile,
        property_type: propertyType,
        property_sub_type: propertySubType || null,
        features: selectedFeatures,
        feature_quantities: featureQuantities,
        features_coefficient: getTotalCoefficient(),
        selected_package: selectedPackage,
        ...(partnerSubdomain && { partner_subdomain: partnerSubdomain }),
      };
      const projectResp = await projectsApi.create(projectData);
      const projectId = projectResp.data.id;

      // ============================================================
      // STEP 3 — Upload all files (videos, docs, photos) BEFORE Stripe
      // ============================================================
      // Videos
      for (const [videoType, fileData] of Object.entries(formData.videos)) {
        if (!fileData) continue;
        if (Array.isArray(fileData)) {
          for (let i = 0; i < fileData.length; i++) {
            toast.info(`Upload vidéo ${i + 1}/${fileData.length}...`);
            await projectsApi.uploadVideo(projectId, `${videoType}_${i}`, fileData[i]);
          }
        } else {
          toast.info(`Upload vidéo: ${VIDEO_TYPES.find(v => v.id === videoType)?.label || videoType}...`);
          await projectsApi.uploadVideo(projectId, videoType, fileData);
        }
      }
      // Documents
      for (const [docType, fileData] of Object.entries(formData.documents)) {
        if (!fileData) continue;
        if (Array.isArray(fileData)) {
          for (let i = 0; i < fileData.length; i++) {
            toast.info(`Upload document ${i + 1}/${fileData.length}...`);
            await projectsApi.uploadVideo(projectId, `doc_${docType}_${i}`, fileData[i]);
          }
        } else {
          toast.info(`Upload document: ${DOCUMENT_TYPES.find(d => d.id === docType)?.label || docType}...`);
          await projectsApi.uploadVideo(projectId, `doc_${docType}`, fileData);
        }
      }
      // Photos
      for (const [photoType, files] of Object.entries(formData.photos)) {
        if (!files || files.length === 0) continue;
        for (let i = 0; i < files.length; i++) {
          toast.info(`Upload photo ${i + 1}/${files.length}...`);
          await projectsApi.uploadVideo(projectId, `photo_${photoType}_${i}`, files[i]);
        }
      }

      // ============================================================
      // STEP 4 — Create Stripe checkout with the existing project_id
      // (verify-and-login will detect it already exists and skip re-creation)
      // ============================================================
      toast.info('Redirection vers le paiement sécurisé...');
      const checkoutResponse = await axios.post(`${API}/payments/checkout`, {
        amount: amount,
        product_name: partnerSubdomain ? `Forfait ${packageName}` : `L'Œil du Marchand - Forfait ${packageName}`,
        origin_url: window.location.origin,
        package_id: selectedPackage,
        project_id: projectId,
        customer_email: email,
        ...(partnerSubdomain && { partner_subdomain: partnerSubdomain }),
        ...(affiliateRef && { referred_by: affiliateRef }),
      });

      if (checkoutResponse.data.url) {
        // Nettoyage du draft local + serveur (le client va payer)
        try { localStorage.removeItem(DRAFT_KEY); } catch {}
        if (draftServerToken) {
          axios.delete(`${API}/audit/draft/${draftServerToken}`).catch(() => {});
        }
        setIsRedirectingToStripe(true);
        try { localStorage.removeItem('affiliate_ref'); } catch {}
        window.location.href = checkoutResponse.data.url;
      } else {
        throw new Error('URL de paiement non reçue');
      }
      
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du paiement');
      isRedirectingToStripeRef.current = false; // Réactive la protection beforeunload si échec
    } finally {
      setLoading(false);
    }
  };

  const createProject = async () => {
    if (!selectedPackage) {
      toast.error('Veuillez choisir un forfait');
      setStep(5);
      return;
    }
    
    // Check if user has credits available
    const userCredits = authUser?.credits ?? 0;
    const hasCredits = userCredits > 0;
    
    setLoading(true);
    // Bloque immédiatement le "beforeunload" pour éviter l'alerte "Quitter le site?"
    // pendant l'upload de fichiers + checkout (avant même la redirection Stripe).
    isRedirectingToStripeRef.current = true;
    try {
      // Create project with selected package
      const projectData = {
        address: formData.address,
        address_precision: formData.address_precision || null,
        latitude: formData.latitude,
        longitude: formData.longitude,
        surface: parseFloat(formData.surface) || 0,
        land_surface: formData.land_surface ? parseFloat(formData.land_surface) : null,
        purchase_price: parseFloat(formData.purchase_price) || 0,
        estimated_resale_price: formData.estimated_resale_price ? parseFloat(formData.estimated_resale_price) : null,
        profile_type: formData.profile_type,
        sale_type: formData.sale_type,
        fiscal_profile: formData.fiscal_profile,
        property_type: propertyType,  // Include property type for DVF data
        property_sub_type: propertySubType || null,
        features: selectedFeatures,
        feature_quantities: featureQuantities,
        features_coefficient: getTotalCoefficient(),
        construction_year: formData.construction_year ? parseInt(formData.construction_year) : null,
        dpe_class: formData.dpe_class || null,
        selected_package: selectedPackage,
        // Coordonnées du client
        email: authUser?.email || formData.email || undefined,
        phone: formData.phone || undefined,
        name: authUser?.full_name || authUser?.name || formData.email_name || undefined,
      };
      
      const response = await projectsApi.create(projectData);
      const projectId = response.data.id;
      
      // Upload videos (including multiple for "autres_videos")
      for (const [videoType, fileData] of Object.entries(formData.videos)) {
        if (fileData) {
          if (Array.isArray(fileData)) {
            // Multiple files
            for (let i = 0; i < fileData.length; i++) {
              toast.info(`Upload vidéo ${i + 1}/${fileData.length}...`);
              await projectsApi.uploadVideo(projectId, `${videoType}_${i}`, fileData[i]);
            }
          } else {
            // Single file
            toast.info(`Upload vidéo: ${VIDEO_TYPES.find(v => v.id === videoType)?.label}...`);
            await projectsApi.uploadVideo(projectId, videoType, fileData);
          }
        }
      }
      
      // Upload documents (including multiple for "autres_docs")
      for (const [docType, fileData] of Object.entries(formData.documents)) {
        if (fileData) {
          if (Array.isArray(fileData)) {
            // Multiple files
            for (let i = 0; i < fileData.length; i++) {
              toast.info(`Upload document ${i + 1}/${fileData.length}...`);
              await projectsApi.uploadVideo(projectId, `doc_${docType}_${i}`, fileData[i]);
            }
          } else {
            // Single file
            toast.info(`Upload document: ${DOCUMENT_TYPES.find(d => d.id === docType)?.label}...`);
            await projectsApi.uploadVideo(projectId, `doc_${docType}`, fileData);
          }
        }
      }
      
      // Upload photos
      for (const [photoType, files] of Object.entries(formData.photos)) {
        if (files && files.length > 0) {
          for (let i = 0; i < files.length; i++) {
            toast.info(`Upload photo ${i + 1}/${files.length}...`);
            await projectsApi.uploadVideo(projectId, `photo_${photoType}_${i}`, files[i]);
          }
        }
      }
      
      // Check if user has credits - if yes, skip payment and go to dashboard
      if (hasCredits) {
        toast.success('✅ Dossier créé avec succès ! (1 crédit utilisé)');
        // Nettoyage du draft
        try { localStorage.removeItem(DRAFT_KEY); } catch {}
        if (draftServerToken) {
          axios.delete(`${API}/audit/draft/${draftServerToken}`).catch(() => {});
        }
        // Redirect to dashboard
        navigate('/dashboard');
        return;
      }
      
      // No credits - redirect to payment
      // Get package price info
      const packageInfo = PACKAGES.find(p => p.id === selectedPackage);
      const amount = packageInfo?.price || 97;
      const packageName = packageInfo?.name || 'Flash';
      
      // Create Stripe checkout session
      toast.info('Redirection vers le paiement sécurisé...');
      const checkoutResponse = await axios.post(`${API}/payments/checkout`, {
        amount: amount,
        product_name: partnerSubdomain ? `Forfait ${packageName}` : `L'Œil du Marchand - Forfait ${packageName}`,
        origin_url: window.location.origin,
        project_id: projectId,
        package_id: selectedPackage,
        customer_email: authUser?.email || formData.email || '',
        ...(partnerSubdomain && { partner_subdomain: partnerSubdomain }),
        ...(affiliateRef && { referred_by: affiliateRef }),
      });
      
      // Redirect to Stripe Checkout
      if (checkoutResponse.data.url) {
        // Nettoyage du draft local + serveur (le client va payer)
        try { localStorage.removeItem(DRAFT_KEY); } catch {}
        if (draftServerToken) {
          axios.delete(`${API}/audit/draft/${draftServerToken}`).catch(() => {});
        }
        setIsRedirectingToStripe(true);
        try { localStorage.removeItem('affiliate_ref'); } catch {}
        window.location.href = checkoutResponse.data.url;
      } else {
        throw new Error('URL de paiement non reçue');
      }
      
    } catch (error) {
      console.error('Project creation error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création du dossier');
      isRedirectingToStripeRef.current = false; // Réactive la protection beforeunload si échec
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // PARTNER FLOW — Soumission gratuite (bypass Stripe)
  // ============================================================

  // Pour les utilisateurs non connectés passant par un lien partenaire
  const handlePartnerDirectSubmit = async () => {
    if (!email || !email.includes('@')) {
      toast.error('Veuillez entrer un email valide');
      return;
    }
    if (!phone || phone.trim().length < 8) {
      toast.error('Le numéro de téléphone est obligatoire');
      return;
    }

    setLoading(true);
    try {
      // Vérification des données essentielles avant envoi
      const surfaceVal = parseFloat(formData.surface);
      const priceVal = parseFloat(formData.purchase_price);
      if (!surfaceVal || surfaceVal <= 0 || !priceVal || priceVal <= 0) {
        toast.error('Données incomplètes. Veuillez vérifier la surface et le prix d\'achat.');
        setStep(3);
        setLoading(false);
        return;
      }

      // Génération d'un mot de passe aléatoire sécurisé (invisible pour le client partenaire)
      const autoPassword = Math.random().toString(36).slice(-10) + Math.random().toString(36).toUpperCase().slice(-4) + '!2';

      // Inscription ou connexion
      let authToken = null;
      try {
        const regRes = await axios.post(`${API}/auth/client/register`, {
          email,
          password: autoPassword,
          name: formData.email_name || '',
          phone: phone.trim(),
          ...(partnerSubdomain && { partner_subdomain: partnerSubdomain }),
        });
        authToken = regRes.data.access_token;
      } catch (regErr) {
        if (regErr.response?.status === 400) {
          // Compte déjà existant — on crée quand même le projet via une route publique
          // en récupérant un token temporaire avec le mot de passe auto n'est pas possible.
          // On continue sans token : le projet sera créé en mode non-authentifié via l'API partenaire.
          authToken = null;
        } else { throw regErr; }
      }

      if (authToken) {
        try { localStorage.setItem('token', authToken); } catch {}
        axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
      }

      // Création du dossier
      toast.info('Création de votre dossier...');
      const projectData = {
        address: formData.address,
        address_precision: formData.address_precision || null,
        latitude: formData.latitude,
        longitude: formData.longitude,
        surface: parseFloat(formData.surface) || 0,
        land_surface: formData.land_surface ? parseFloat(formData.land_surface) : null,
        purchase_price: parseFloat(formData.purchase_price) || 0,
        estimated_resale_price: formData.estimated_resale_price ? parseFloat(formData.estimated_resale_price) : null,
        profile_type: formData.profile_type,
        sale_type: formData.sale_type,
        fiscal_profile: formData.fiscal_profile,
        property_type: propertyType,
        property_sub_type: propertySubType || null,
        features: selectedFeatures,
        feature_quantities: featureQuantities,
        features_coefficient: getTotalCoefficient(),
        phone: phone.trim(),
        partner_subdomain: partnerSubdomain,
        email: email,
        name: formData.email_name || '',
      };
      const projectResp = await projectsApi.create(projectData);
      const projectId = projectResp.data.id;

      // Upload des fichiers
      for (const [videoType, fileData] of Object.entries(formData.videos)) {
        if (!fileData) continue;
        if (Array.isArray(fileData)) {
          for (let i = 0; i < fileData.length; i++) { toast.info(`Upload vidéo ${i+1}/${fileData.length}...`); await projectsApi.uploadVideo(projectId, `${videoType}_${i}`, fileData[i]); }
        } else { toast.info(`Upload vidéo...`); await projectsApi.uploadVideo(projectId, videoType, fileData); }
      }
      for (const [docType, fileData] of Object.entries(formData.documents)) {
        if (!fileData) continue;
        if (Array.isArray(fileData)) {
          for (let i = 0; i < fileData.length; i++) { toast.info(`Upload document ${i+1}/${fileData.length}...`); await projectsApi.uploadVideo(projectId, `doc_${docType}_${i}`, fileData[i]); }
        } else { await projectsApi.uploadVideo(projectId, `doc_${docType}`, fileData); }
      }
      for (const [photoType, files] of Object.entries(formData.photos)) {
        if (!files || files.length === 0) continue;
        for (let i = 0; i < files.length; i++) { toast.info(`Upload photo ${i+1}/${files.length}...`); await projectsApi.uploadVideo(projectId, `photo_${photoType}_${i}`, files[i]); }
      }

      // Soumission partenaire (bypass Stripe)
      await axios.post(`${API}/projects/${projectId}/partner-submit`);
      try { localStorage.removeItem(DRAFT_KEY); } catch {}
      if (draftServerToken) { axios.delete(`${API}/audit/draft/${draftServerToken}`).catch(() => {}); }
      // Afficher l'écran de succès (pas de redirection vers le dashboard)
      setPartnerSubmitSuccess(true);
    } catch (error) {
      console.error('Partner submit error:', error);
      toast.error(
        Array.isArray(error.response?.data?.detail)
          ? 'Données invalides. Vérifiez la surface et le prix à l\'étape 3.'
          : (error.response?.data?.detail || 'Erreur lors de la soumission. Réessayez.')
      );
    } finally {
      setLoading(false);
    }
  };

  // Pour les utilisateurs déjà connectés passant par un lien partenaire
  const createPartnerProject = async () => {
    setLoading(true);
    try {
      // Vérification des données essentielles avant envoi
      const surfaceVal = parseFloat(formData.surface);
      const priceVal = parseFloat(formData.purchase_price);
      if (!surfaceVal || surfaceVal <= 0 || !priceVal || priceVal <= 0) {
        toast.error('Données incomplètes. Veuillez vérifier la surface et le prix d\'achat.');
        setStep(3);
        setLoading(false);
        return;
      }
      toast.info('Création de votre dossier...');
      const projectData = {
        address: formData.address,
        address_precision: formData.address_precision || null,
        latitude: formData.latitude,
        longitude: formData.longitude,
        surface: parseFloat(formData.surface) || 0,
        land_surface: formData.land_surface ? parseFloat(formData.land_surface) : null,
        purchase_price: parseFloat(formData.purchase_price) || 0,
        estimated_resale_price: formData.estimated_resale_price ? parseFloat(formData.estimated_resale_price) : null,
        profile_type: formData.profile_type,
        sale_type: formData.sale_type,
        fiscal_profile: formData.fiscal_profile,
        property_type: propertyType,
        property_sub_type: propertySubType || null,
        features: selectedFeatures,
        feature_quantities: featureQuantities,
        features_coefficient: getTotalCoefficient(),
        partner_subdomain: partnerSubdomain,
      };
      const response = await projectsApi.create(projectData);
      const projectId = response.data.id;

      // Upload des fichiers
      for (const [videoType, fileData] of Object.entries(formData.videos)) {
        if (!fileData) continue;
        if (Array.isArray(fileData)) {
          for (let i = 0; i < fileData.length; i++) { toast.info(`Upload vidéo ${i+1}/${fileData.length}...`); await projectsApi.uploadVideo(projectId, `${videoType}_${i}`, fileData[i]); }
        } else { await projectsApi.uploadVideo(projectId, videoType, fileData); }
      }
      for (const [docType, fileData] of Object.entries(formData.documents)) {
        if (!fileData) continue;
        if (Array.isArray(fileData)) {
          for (let i = 0; i < fileData.length; i++) { await projectsApi.uploadVideo(projectId, `doc_${docType}_${i}`, fileData[i]); }
        } else { await projectsApi.uploadVideo(projectId, `doc_${docType}`, fileData); }
      }
      for (const [photoType, files] of Object.entries(formData.photos)) {
        if (!files || files.length === 0) continue;
        for (let i = 0; i < files.length; i++) { await projectsApi.uploadVideo(projectId, `photo_${photoType}_${i}`, files[i]); }
      }

      // Soumission partenaire (bypass Stripe)
      await axios.post(`${API}/projects/${projectId}/partner-submit`);
      try { localStorage.removeItem(DRAFT_KEY); } catch {}
      if (draftServerToken) { axios.delete(`${API}/audit/draft/${draftServerToken}`).catch(() => {}); }
      // Afficher l'écran de succès (pas de redirection vers le dashboard)
      setPartnerSubmitSuccess(true);
    } catch (error) {
      console.error('Partner project creation error:', error);
      toast.error(
        Array.isArray(error.response?.data?.detail)
          ? 'Données invalides. Vérifiez la surface et le prix à l\'étape 3.'
          : (error.response?.data?.detail || 'Erreur lors de la soumission. Réessayez.')
      );
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    switch (step) {
      case 1: return formData.address.length >= 5;
      case 2: return !!formData.sale_type_id;
      case 3: return formData.surface && formData.purchase_price;
      case 4: return true;
      case 5:
        if (partnerSubdomain) {
          if (isAuthenticated) return true;
          return email.includes('@') && phone.trim().length >= 8;
        }
        return selectedPackage !== null;
      default: return false;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <header
        className={`text-white py-3 sm:py-4 px-3 sm:px-6 shadow-lg ${!partnerInfo ? 'bg-navy' : ''}`}
        style={partnerInfo ? { backgroundColor: partnerInfo.primary_color || partnerInfo.brand_color || '#0A192F' } : undefined}
      >
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="min-w-0 flex items-center gap-3">
            {partnerInfo?.logo_url && (
              <img
                src={partnerInfo.logo_url}
                alt={partnerInfo.name || 'Partenaire'}
                className="h-9 sm:h-11 w-9 sm:w-11 rounded-lg object-cover bg-white/10 shrink-0"
                data-testid="collector-partner-logo"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            )}
            <div className="min-w-0">
              <h1 className="font-heading text-base sm:text-xl font-bold tracking-tight truncate" data-testid="collector-brand-name">
                {(partnerInfo?.name || partnerInfo?.full_name || "L'ŒIL DU MARCHAND").toString().toUpperCase()}
              </h1>
              <p className="text-[10px] sm:text-xs text-white/60 mt-0.5 truncate">Audit immobilier • Expertise</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-3 shrink-0">
            <InstallPwaButton appName={partnerInfo?.name || "L'Œil du Marchand"} color={partnerInfo ? 'rgba(255,255,255,0.22)' : '#F59E0B'} />
            {isAuthenticated ? (
              <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} className="text-white hover:bg-white/10 text-xs sm:text-sm">
                Mon Espace
              </Button>
            ) : (
              <>
                <Button 
                  data-testid="auth-btn"
                  variant="outline" 
                  size="sm" 
                  onClick={() => navigate(withPartner('/auth', partnerSubdomain))} 
                  className="border-white/30 text-white hover:bg-white/10 text-xs sm:text-sm px-2 sm:px-3"
                >
                  Connexion
                </Button>
                {!partnerSubdomain && (
                <Button 
                  data-testid="admin-access-header-btn"
                  variant="ghost" 
                  size="sm" 
                  onClick={() => navigate('/espace-pro')} 
                  className="text-gold hover:bg-gold/10 text-xs sm:text-sm px-2 sm:px-3"
                >
                  <span className="hidden sm:inline">Espace Pro</span>
                  <span className="sm:hidden">Pro</span>
                </Button>
                )}
              </>
            )}
          </div>
        </div>
      </header>

      {/* Expert Badge - Visible on all steps */}
      <div className="bg-white border-b py-3">
        <div className="max-w-4xl mx-auto px-6 flex justify-center">
          <ExpertBadge showNetwork={true} />
        </div>
      </div>

      {/* Mode Tabs - Responsive grid layout for mobile */}
      <div className="bg-white border-b">
        <div className="max-w-5xl mx-auto px-4 md:px-6 py-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
            {/* 1. Estimation Valeur */}
            <button
              data-testid="mode-estimation-tab"
              onClick={() => switchMode('estimation')}
              className={`rounded-xl p-3 md:p-4 text-center font-semibold transition-all border-2 shadow-sm hover:shadow-md ${
                mode === 'estimation' 
                  ? 'border-emerald-500 bg-emerald-50 ring-2 ring-emerald-500/30' 
                  : 'border-gray-200 bg-white hover:border-emerald-300 hover:bg-emerald-50/50'
              }`}
            >
              <div className="flex flex-col items-center gap-1.5">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${mode === 'estimation' ? 'bg-emerald-500 text-white' : 'bg-emerald-100 text-emerald-600'}`}>
                  <TrendingUp className="w-5 h-5" />
                </div>
                <span className="text-sm md:text-base text-navy">Estimation</span>
                <Badge className="bg-emerald-100 text-emerald-700 text-xs">Gratuit</Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-2 hidden md:block">Prix au m² réel</p>
            </button>

            {/* 2. Radar Foncier (déplacé en 2ème position) */}
            <button
              data-testid="mode-radar-tab"
              onClick={() => navigate(withPartner('/radar', partnerSubdomain))}
              className="rounded-xl p-3 md:p-4 text-center font-semibold transition-all border-2 shadow-sm hover:shadow-md border-gray-200 bg-white hover:border-purple-300 hover:bg-purple-50/50"
            >
              <div className="flex flex-col items-center gap-1.5">
                <div className="w-10 h-10 rounded-full flex items-center justify-center bg-purple-100 text-purple-600">
                  <MapPin className="w-5 h-5" />
                </div>
                <span className="text-sm md:text-base text-navy">Radar Foncier</span>
                <Badge className="bg-purple-100 text-purple-700 text-xs">Gratuit</Badge>
              </div>
              {!partnerSubdomain && <p className="text-xs text-muted-foreground mt-2 hidden md:block">Trouvez le propriétaire</p>}
            </button>

            {/* 3. Audit Complet */}
            <button
              data-testid="mode-audit-tab"
              onClick={() => switchMode('audit')}
              className={`rounded-xl p-3 md:p-4 text-center font-semibold transition-all border-2 shadow-sm hover:shadow-md ${
                mode === 'audit' 
                  ? 'border-navy bg-navy/5 ring-2 ring-navy/30' 
                  : 'border-gray-200 bg-white hover:border-navy/50 hover:bg-navy/5'
              }`}
            >
              <div className="flex flex-col items-center gap-1.5">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${mode === 'audit' ? 'bg-navy text-white' : 'bg-navy/10 text-navy'}`}>
                  <Target className="w-5 h-5" />
                </div>
                <span className="text-sm md:text-base text-navy">{partnerSubdomain ? 'Audit Gratuite' : 'Audit Complet'}</span>
                <Badge className={partnerSubdomain ? 'bg-emerald-100 text-emerald-700 text-xs' : 'bg-navy/10 text-navy text-xs'}>{partnerSubdomain ? 'Gratuit' : 'Pro'}</Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-2 hidden md:block">Analyse technique</p>
            </button>

            {/* 4. Conseil Stratégique & Suivi de Travaux VIP */}
            <button
              data-testid="mode-chantier-tab"
              onClick={() => switchMode('chantier')}
              className={`rounded-xl p-3 md:p-4 text-center font-semibold transition-all border-2 shadow-sm hover:shadow-md ${
                mode === 'chantier' 
                  ? 'border-gold bg-gold/10 ring-2 ring-gold/30' 
                  : 'border-gray-200 bg-white hover:border-gold/50 hover:bg-gold/5'
              }`}
            >
              <div className="flex flex-col items-center gap-1">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${mode === 'chantier' ? 'bg-gold text-black' : 'bg-gold/20 text-gold'}`}>
                  <Shield className="w-5 h-5" />
                </div>
                <span className="text-xs md:text-sm text-navy leading-tight">Conseil Stratégique</span>
                <Badge className="bg-gold text-black text-xs">VIP</Badge>
                <span className="text-xs md:text-sm text-navy leading-tight">Suivi de Travaux</span>
              </div>
            </button>
          </div>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-6 py-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-navy">
              Étape {mode === 'audit' ? step : estimationStep}/{mode === 'audit' ? totalSteps : totalEstimationSteps}
            </span>
            <span className="text-sm text-muted-foreground">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>
      </div>

      {/* === BANDEAU DE REPRISE D'AUDIT (masqué pour les partenaires) === */}
      {showDraftBanner && mode === 'audit' && !partnerSubdomain && (
        <div className="bg-amber-50 border-b border-amber-300" data-testid="draft-banner">
          <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <span className="text-2xl">⏰</span>
              <div>
                <p className="font-semibold text-amber-900 text-sm">Vous avez un audit en cours sur ce navigateur</p>
                <p className="text-xs text-amber-700">Voulez-vous le reprendre où vous vous êtes arrêté ?</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={restoreDraft} data-testid="restore-draft-btn" className="bg-emerald-500 hover:bg-emerald-600 text-white px-4 py-2 rounded-lg text-sm font-semibold">
                Reprendre
              </button>
              <button onClick={discardDraft} data-testid="discard-draft-btn" className="bg-white hover:bg-gray-50 border border-gray-300 text-gray-700 px-4 py-2 rounded-lg text-sm">
                Recommencer
              </button>
            </div>
          </div>
        </div>
      )}

      {/* === BOUTON "Sauvegarder & recevoir un lien de reprise par email" === */}
      {mode === 'audit' && step >= 2 && formData.email && !draftServerToken && (
        <div className="bg-blue-50 border-b border-blue-200">
          <div className="max-w-4xl mx-auto px-6 py-2 flex items-center justify-between flex-wrap gap-2">
            <p className="text-xs text-blue-900">
              💾 Vous voulez interrompre et reprendre depuis un autre appareil ? Recevez un lien de reprise par email.
            </p>
            <button
              onClick={saveDraftOnServer}
              data-testid="save-draft-server-btn"
              className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3 py-1.5 rounded font-semibold"
            >
              📧 Sauvegarder & recevoir le lien
            </button>
          </div>
        </div>
      )}
      {mode === 'audit' && draftServerToken && (
        <div className="bg-emerald-50 border-b border-emerald-200">
          <div className="max-w-4xl mx-auto px-6 py-2 text-center">
            <p className="text-xs text-emerald-900">✅ Audit sauvegardé sur le serveur — un email avec un lien de reprise vient de vous être envoyé.</p>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="animate-fade-in">
          
          {/* ==================== ESTIMATION MODE (Lead Magnet) ==================== */}
          {mode === 'estimation' && partnerInfo?.country === 'algerie' && (
            /* ===== FLOW ESTIMATION ALGÉRIE ===== */
            <>
              {/* Step 1 : Sélection zone */}
              {algeriaStep === 1 && (
                <div className="space-y-6">
                  <div className="text-center mb-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 mb-4">
                      <MapPin className="w-8 h-8 text-emerald-600" />
                    </div>
                    <h2 className="font-heading text-2xl font-bold text-navy">🇩🇿 Estimation gratuite</h2>
                    <p className="text-muted-foreground mt-2">Prix en Dinars Algériens (DZD) basés sur le marché local</p>
                  </div>
                  <Card className="border-2 border-emerald-200 shadow-lg">
                    <CardContent className="p-6">
                      <label className="text-navy font-semibold text-base mb-3 block">Sélectionnez votre zone</label>
                      <div className="grid grid-cols-1 gap-2">
                        {(algeriaPriceGrid?.zones || [
                          { key: 'alger_centre', label: 'Alger Centre (Hydra, El Biar, Bouzareah, Chéraga)' },
                          { key: 'alger_peripherie', label: 'Alger Périphérie (Kouba, Bir Mourad Raïs, Bab Ezzouar)' },
                          { key: 'oran_centre', label: 'Oran Centre' },
                          { key: 'oran_peripherie', label: 'Oran Périphérie (Bir El Djir, Es Sénia)' },
                          { key: 'constantine', label: 'Constantine & périphérie' },
                          { key: 'annaba', label: 'Annaba' },
                          { key: 'setif', label: 'Sétif' },
                          { key: 'tlemcen', label: 'Tlemcen' },
                          { key: 'autres_villes', label: 'Autres villes (Blida, Boumerdès, Tizi Ouzou...)' },
                        ]).map(z => (
                          <button key={z.key} type="button"
                            onClick={() => setAlgeriaZone(z.key)}
                            className={`p-3 rounded-lg border-2 text-left text-sm transition-all ${algeriaZone === z.key ? 'border-emerald-500 bg-emerald-50 font-medium' : 'border-gray-200 hover:border-emerald-300'}`}
                            data-testid={`algeria-zone-${z.key}`}>
                            {z.label}
                          </button>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                  <div className="flex justify-end">
                    <Button onClick={() => setAlgeriaStep(2)} disabled={!algeriaZone} className="bg-emerald-600 hover:bg-emerald-700 text-white px-8" data-testid="algeria-next-1">
                      Continuer <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 2 : Type de bien + surface */}
              {algeriaStep === 2 && (
                <div className="space-y-6">
                  <div className="text-center mb-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 mb-4">
                      <Home className="w-8 h-8 text-emerald-600" />
                    </div>
                    <h2 className="font-heading text-2xl font-bold text-navy">Votre bien</h2>
                  </div>
                  <Card className="border-2 border-emerald-200 shadow-lg">
                    <CardContent className="p-6 space-y-4">
                      <div>
                        <label className="text-navy font-semibold text-base mb-3 block">Type de bien</label>
                        <div className="grid grid-cols-2 gap-2">
                          {(algeriaPriceGrid?.property_types || [
                            { key: 'appartement', label: 'Appartement' },
                            { key: 'villa', label: 'Villa / Maison' },
                            { key: 'local_commercial', label: 'Local Commercial' },
                            { key: 'terrain', label: 'Terrain' },
                          ]).map(t => (
                            <button key={t.key} type="button"
                              onClick={() => setAlgeriaPropertyType(t.key)}
                              className={`p-3 rounded-lg border-2 text-sm transition-all ${algeriaPropertyType === t.key ? 'border-emerald-500 bg-emerald-50 font-medium' : 'border-gray-200 hover:border-emerald-300'}`}
                              data-testid={`algeria-type-${t.key}`}>
                              {t.label}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div>
                        <label className="text-navy font-semibold text-base mb-2 block">Surface (m²)</label>
                        <input type="number" value={algeriaSurface} onChange={e => setAlgeriaSurface(e.target.value)}
                          className="w-full border-2 border-emerald-200 rounded-lg px-4 py-3 text-lg focus:outline-none focus:border-emerald-500"
                          placeholder="Ex: 80" min="1" data-testid="algeria-surface-input" />
                      </div>
                    </CardContent>
                  </Card>
                  <div className="flex justify-between">
                    <Button variant="outline" onClick={() => setAlgeriaStep(1)}>Retour</Button>
                    <Button onClick={() => setAlgeriaStep(3)} disabled={!algeriaPropertyType || !algeriaSurface || parseFloat(algeriaSurface) <= 0} className="bg-emerald-600 hover:bg-emerald-700 text-white px-8" data-testid="algeria-next-2">
                      Continuer <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 3 : Email + Téléphone */}
              {algeriaStep === 3 && (
                <div className="space-y-6">
                  <div className="text-center mb-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 mb-4">
                      <Mail className="w-8 h-8 text-emerald-600" />
                    </div>
                    <h2 className="font-heading text-2xl font-bold text-navy">Recevoir l'estimation</h2>
                    <p className="text-muted-foreground mt-2">Votre estimation gratuite par email en quelques secondes</p>
                  </div>
                  <Card className="border-2 border-emerald-200 shadow-lg">
                    <CardContent className="p-6 space-y-4">
                      <div>
                        <label className="text-navy font-semibold mb-2 block">Email *</label>
                        <input type="email" value={algeriaEmail} onChange={e => setAlgeriaEmail(e.target.value)}
                          className="w-full border-2 border-emerald-200 rounded-lg px-4 py-3 focus:outline-none focus:border-emerald-500"
                          placeholder="votre@email.com" data-testid="algeria-email-input" />
                      </div>
                      <div>
                        <label className="text-navy font-semibold mb-2 block">Téléphone (optionnel)</label>
                        <input type="tel" value={algeriaPhone} onChange={e => setAlgeriaPhone(e.target.value)}
                          className="w-full border-2 border-emerald-200 rounded-lg px-4 py-3 focus:outline-none focus:border-emerald-500"
                          placeholder="0550 00 00 00" data-testid="algeria-phone-input" />
                      </div>
                    </CardContent>
                  </Card>
                  <div className="flex justify-between">
                    <Button variant="outline" onClick={() => setAlgeriaStep(2)}>Retour</Button>
                    <Button onClick={getAlgeriaEstimation} disabled={algeriaLoading || !algeriaEmail} className="bg-emerald-600 hover:bg-emerald-700 text-white px-8" data-testid="algeria-get-estimate-btn">
                      {algeriaLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Calcul...</> : <>Obtenir mon estimation <ArrowRight className="w-4 h-4 ml-2" /></>}
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 4 : Résultat DZD */}
              {algeriaStep === 4 && algeriaResult && (
                <div className="space-y-6">
                  <div className="text-center mb-6">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 mb-4">
                      <CheckCircle className="w-8 h-8 text-emerald-600" />
                    </div>
                    <h2 className="font-heading text-2xl font-bold text-navy">Votre estimation</h2>
                    <p className="text-muted-foreground mt-2">Résultat envoyé sur {algeriaEmail}</p>
                  </div>
                  <Card className="border-2 border-emerald-500 shadow-xl">
                    <CardContent className="p-6">
                      <div className="bg-emerald-600 text-white rounded-xl p-6 text-center mb-6">
                        <p className="text-sm opacity-80 mb-1">Valeur estimée</p>
                        <p className="text-4xl font-bold mb-1">{parseInt(algeriaResult.estimated_price).toLocaleString('fr-FR')} DZD</p>
                        <p className="text-sm opacity-75">Fourchette : {parseInt(algeriaResult.estimated_min).toLocaleString('fr-FR')} — {parseInt(algeriaResult.estimated_max).toLocaleString('fr-FR')} DZD</p>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between py-2 border-b"><span className="text-gray-600">Zone</span><span className="font-medium">{algeriaResult.zone_label}</span></div>
                        <div className="flex justify-between py-2 border-b"><span className="text-gray-600">Type</span><span className="font-medium">{algeriaResult.property_type?.replace('_', ' ')}</span></div>
                        <div className="flex justify-between py-2 border-b"><span className="text-gray-600">Surface</span><span className="font-medium">{algeriaResult.surface} m²</span></div>
                        <div className="flex justify-between py-2"><span className="text-gray-600">Prix moyen/m²</span><span className="font-medium">{parseInt(algeriaResult.price_per_m2_avg).toLocaleString('fr-FR')} DZD</span></div>
                      </div>
                      <p className="text-xs text-gray-400 mt-4 text-center">Estimation indicative basée sur le marché algérien (Ouedkniss 2025)</p>
                    </CardContent>
                  </Card>
                  <Button variant="outline" className="w-full" onClick={() => { setAlgeriaStep(1); setAlgeriaResult(null); setAlgeriaZone(''); setAlgeriaEmail(''); setAlgeriaPhone(''); }}>
                    Nouvelle estimation
                  </Button>
                </div>
              )}
            </>
          )}

          {mode === 'estimation' && partnerInfo?.country !== 'algerie' && (partnerInfo !== null || !partnerSubdomain) && (
            <>
              {/* Estimation Step 1: Adresse uniquement (simplifié mobile) */}
              {estimationStep === 1 && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 mb-4">
                      <MapPin className="w-8 h-8 text-emerald-600" />
                    </div>
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-navy">Estimation gratuite</h2>
                    <p className="text-muted-foreground mt-2 max-w-md mx-auto">
                      Obtenez le <span className="text-emerald-600 font-semibold">prix au m² réel</span> basé sur les ventes des 3 dernières années
                    </p>
                  </div>

                  {/* Address Input */}
                  <Card className="border-2 border-emerald-200 shadow-lg overflow-visible">
                    <CardContent className="p-6 overflow-visible">
                      <Label htmlFor="estimation-address" className="text-navy font-semibold text-base mb-4 block">
                        Adresse du bien à estimer
                      </Label>
                      <div className="relative">
                        <AddressInput
                          value={formData.address}
                          onChange={(val) => setFormData(prev => ({ ...prev, address: val }))}
                          onSelect={selectAddress}
                          placeholder="Ex: 12 Rue de la Paix, 75002 Paris"
                          className="text-lg py-3 px-4 w-full border border-emerald-200 rounded-lg focus:ring-2 focus:ring-emerald-200 focus:border-emerald-500 outline-none"
                          testId="estimation-address-input"
                        />
                      </div>
                    </CardContent>
                  </Card>

                  <div className="flex justify-end">
                    <Button
                      data-testid="estimation-next-1"
                      onClick={() => setEstimationStep(2)}
                      disabled={formData.address.length < 5}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white px-8"
                    >
                      Continuer
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Estimation Step 2: Détails du bien (Type + sous-type + caractéristiques + surface) */}
              {estimationStep === 2 && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 mb-4">
                      <Home className="w-8 h-8 text-emerald-600" />
                    </div>
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-navy">Détails du bien</h2>
                    <p className="text-muted-foreground mt-2">{formData.address}</p>
                  </div>

                  {/* Property Type Selector */}
                  <Card className="border-2 border-emerald-200 shadow-lg">
                    <CardContent className="p-6">
                      <Label className="text-navy font-semibold text-base mb-4 block">
                        Type de bien
                      </Label>
                      <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                        {PROPERTY_TYPES.map((type) => (
                          <button
                            key={type.id}
                            data-testid={`property-type-${type.id}`}
                            onClick={() => { setPropertyType(type.id); setPropertySubType(''); setSelectedFeatures([]); setVueCategory(null); setVueSub(null); setVueExpanded(false); }}
                            className={`p-2 rounded-lg border-2 transition-all text-center ${
                              propertyType === type.id 
                                ? 'border-emerald-500 bg-emerald-50 ring-1 ring-emerald-500' 
                                : 'border-gray-200 hover:border-emerald-300'
                            }`}
                          >
                            <span className="text-xl block mb-0.5">{type.icon}</span>
                            <span className="text-[10px] md:text-xs font-medium text-navy leading-tight block">{type.label}</span>
                          </button>
                        ))}
                      </div>
                    </CardContent>
                  </Card>

                  {/* Sub-type selection */}
                  {PROPERTY_SUBCATEGORIES[propertyType] && (
                    <Card className="border-2 border-emerald-200 shadow-lg">
                      <CardContent className="p-6">
                        <Label className="text-navy font-semibold text-base mb-4 block">
                          Précisez le type
                        </Label>
                        <div className="flex flex-wrap gap-2">
                          {PROPERTY_SUBCATEGORIES[propertyType].map((sub) => (
                            <button
                              key={sub.id}
                              data-testid={`property-subtype-${sub.id}`}
                              onClick={() => setPropertySubType(sub.id)}
                              className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                                propertySubType === sub.id
                                  ? 'border-emerald-500 bg-emerald-50 text-emerald-700 ring-1 ring-emerald-500'
                                  : 'border-gray-200 text-gray-600 hover:border-emerald-300 hover:bg-emerald-50/50'
                              }`}
                            >
                              {sub.label}
                            </button>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Property Features */}
                  {PROPERTY_FEATURES[propertyType] && (
                    <Card className="border-2 border-emerald-200 shadow-lg">
                      <CardContent className="p-6">
                        <div className="flex items-center justify-between mb-4">
                          <Label className="text-navy font-semibold text-base">
                            Caractéristiques du bien
                          </Label>
                          {selectedFeatures.length > 0 && (
                            <Badge className="bg-emerald-100 text-emerald-700 text-xs font-bold">
                              {selectedFeatures.length} sélectionnée{selectedFeatures.length > 1 ? 's' : ''}
                            </Badge>
                          )}
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                          {PROPERTY_FEATURES[propertyType].map((feature) => {
                            const isSelected = selectedFeatures.includes(feature.id);
                            const isQuantifiable = feature.inputType === 'quantity';
                            const isSurface = feature.inputType === 'surface';

                            if (feature.inputType === 'vue_type') {
                              const currentCat = vueOptionsData.find(c => c.id === vueCategory);
                              return (
                                <div key={feature.id} className="col-span-2 md:col-span-3 flex flex-col">
                                  <button data-testid="feature-vue_type"
                                    onClick={() => setVueExpanded(!vueExpanded)}
                                    className={`flex items-center gap-2 px-3 py-2.5 border-2 text-sm transition-all text-left ${(vueExpanded || vueSub) ? 'rounded-t-lg rounded-b-none border-b-0' : 'rounded-lg'} ${vueSub ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'}`}>
                                    <span className="text-lg shrink-0">🏔️</span>
                                    <span className="flex-1 font-medium text-xs">
                                      {vueSub ? `Vue : ${vueSub.label} (+${vueSub.coef}%)` : 'Type de Vue'}
                                    </span>
                                    {vueSub
                                      ? <button type="button" onClick={(e) => { e.stopPropagation(); setVueSub(null); setVueCategory(null); setVueExpanded(false); }} className="text-xs px-1.5 py-0.5 rounded hover:bg-red-100 text-red-500">✕</button>
                                      : <span className="text-xs text-gray-400">{vueExpanded ? '▲' : '▼'}</span>
                                    }
                                  </button>
                                  {(vueExpanded || vueSub) && (
                                    <div className={`border-2 border-t-0 rounded-b-lg p-3 space-y-3 ${vueSub ? 'border-emerald-500 bg-emerald-50/50' : 'border-gray-200 bg-gray-50'}`}>
                                      <div>
                                        <p className="text-xs font-medium text-gray-500 mb-2">Catégorie</p>
                                        <div className="grid grid-cols-2 gap-1.5">
                                          {vueOptionsData.map(cat => (
                                            <button key={cat.id} type="button"
                                              onClick={() => { setVueCategory(cat.id); setVueSub(null); }}
                                              className={`flex flex-col items-start p-2 rounded-lg border text-xs transition-all ${vueCategory === cat.id ? 'border-emerald-500 bg-emerald-100' : 'border-gray-200 hover:border-gray-300 bg-white'}`}>
                                              <span className="font-medium">{cat.icon} {cat.label}</span>
                                              <span className="text-gray-400 text-[10px] mt-0.5 leading-tight">{cat.subtitle}</span>
                                            </button>
                                          ))}
                                        </div>
                                      </div>
                                      {currentCat && (
                                        <div>
                                          <p className="text-xs font-medium text-gray-500 mb-2">Précisez :</p>
                                          <div className="space-y-1">
                                            {currentCat.sub.map(sub => (
                                              <label key={sub.id}
                                                className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer border text-xs transition-all ${vueSub?.id === sub.id ? 'border-emerald-500 bg-emerald-100' : 'border-transparent hover:bg-gray-100'}`}>
                                                <input type="radio" name="vue_sub_collector" checked={vueSub?.id === sub.id}
                                                  onChange={() => { setVueSub(sub); setVueExpanded(false); }}
                                                  className="accent-emerald-500" />
                                                <span className="flex-1">{sub.label}</span>
                                              </label>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            }

                            return (
                              <div key={feature.id} className="flex flex-col">
                                <button
                                  data-testid={`feature-${feature.id}`}
                                  onClick={() => toggleFeature(feature.id)}
                                  className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border-2 text-sm transition-all text-left ${
                                    isSelected
                                      ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                                      : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                                  } ${isSelected && (isQuantifiable || isSurface) ? 'rounded-b-none border-b-0' : ''}`}
                                >
                                  <span className="text-lg shrink-0">{feature.icon}</span>
                                  <span className="flex-1 font-medium text-xs">{feature.label}</span>
                                  {isSelected && <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />}
                                </button>
                                {isSelected && isQuantifiable && (
                                  <div className="flex items-center justify-between border-2 border-t-0 border-emerald-500 bg-emerald-50 rounded-b-lg px-3 py-1.5">
                                    <button type="button" onClick={(e) => { e.stopPropagation(); updateFeatureQuantity(feature.id, (featureQuantities[feature.id] || 1) - 1); }} className="w-7 h-7 rounded-full bg-white border border-emerald-300 flex items-center justify-center text-emerald-700 font-bold text-sm hover:bg-emerald-100">-</button>
                                    <span className="text-sm font-bold text-emerald-700">{featureQuantities[feature.id] || 1}</span>
                                    <button type="button" onClick={(e) => { e.stopPropagation(); updateFeatureQuantity(feature.id, (featureQuantities[feature.id] || 1) + 1); }} className="w-7 h-7 rounded-full bg-white border border-emerald-300 flex items-center justify-center text-emerald-700 font-bold text-sm hover:bg-emerald-100">+</button>
                                  </div>
                                )}
                                {isSelected && isSurface && (
                                  <div className="flex items-center border-2 border-t-0 border-emerald-500 bg-emerald-50 rounded-b-lg px-3 py-1.5 gap-2">
                                    <input
                                      type="number"
                                      value={featureQuantities[feature.id] || ''}
                                      onChange={(e) => updateFeatureQuantity(feature.id, parseInt(e.target.value) || 0)}
                                      onClick={(e) => e.stopPropagation()}
                                      placeholder="m²"
                                      className="w-full text-center text-sm font-bold bg-white border border-emerald-300 rounded px-2 py-1 text-emerald-700 outline-none focus:ring-1 focus:ring-emerald-400"
                                    />
                                    <span className="text-xs text-emerald-600 font-medium whitespace-nowrap">m²</span>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Surface */}
                  <Card className="border-2 border-emerald-200 shadow-lg">
                    <CardContent className="p-6">
                      <Label className="text-navy font-semibold text-base mb-4 block">
                        Surface habitable en m²
                      </Label>
                      <Input
                        data-testid="estimation-surface-input"
                        type="number"
                        value={formData.surface}
                        onChange={(e) => setFormData({ ...formData, surface: e.target.value })}
                        placeholder="Ex: 75"
                        className="text-lg py-6 border-emerald-200 focus:border-emerald-500 text-center text-2xl"
                      />
                    </CardContent>
                  </Card>

                  {/* Surface Terrain — visible uniquement pour Maison/Villa/Local/Entrepot/Terrain */}
                  {['maison', 'local', 'entrepot', 'terrain'].includes(propertyType) && (
                    <Card className="border-2 border-emerald-200 shadow-lg">
                      <CardContent className="p-6">
                        <Label className="text-navy font-semibold text-base mb-2 block">
                          Surface du terrain en m² {propertyType !== 'terrain' && <span className="text-xs text-muted-foreground font-normal">(facultatif mais recommandé)</span>}
                        </Label>
                        <p className="text-xs text-muted-foreground mb-3">
                          <strong>Surface totale de la parcelle cadastrale</strong> (comme indiqué sur votre titre de propriété). Ex : une parcelle de 400 m² avec une maison de 100 m² dessus → tapez 400 ici.
                        </p>
                        <Input
                          data-testid="estimation-land-surface-input"
                          type="number"
                          value={formData.land_surface}
                          onChange={(e) => setFormData({ ...formData, land_surface: e.target.value })}
                          placeholder="Ex: 250"
                          className="text-lg py-6 border-emerald-200 focus:border-emerald-500 text-center text-2xl"
                        />
                      </CardContent>
                    </Card>
                  )}

                  <div className="flex justify-between">
                    <Button variant="outline" onClick={() => setEstimationStep(1)}>
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Retour
                    </Button>
                    <Button
                      data-testid="estimation-next-2"
                      onClick={() => setEstimationStep(3)}
                      disabled={!formData.surface}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white px-8"
                    >
                      Continuer
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Estimation Step 3: Email + Téléphone (capture du lead avant résultat) */}
              {estimationStep === 3 && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 mb-4">
                      <Mail className="w-10 h-10 text-white" />
                    </div>
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-navy">Vos coordonnées</h2>
                    <p className="text-muted-foreground mt-2 max-w-md mx-auto">
                      Renseignez vos coordonnées pour recevoir votre <span className="text-emerald-600 font-semibold">estimation personnalisée</span>
                    </p>
                  </div>

                  <Card className="border-2 border-gold shadow-lg bg-gradient-to-br from-gold/5 to-white">
                    <CardContent className="p-8">
                      <div className="flex items-center gap-2 mb-6 justify-center">
                        <Award className="w-6 h-6 text-gold" />
                        <p className="text-sm font-medium text-navy">
                          Vous recevrez les <span className="font-semibold text-gold">conseils de l'Expert</span> (25 ans d'expérience)
                        </p>
                      </div>
                      
                      <div className="max-w-md mx-auto space-y-4">
                        <div>
                          <Label className="text-navy font-semibold text-sm mb-2 block">Email</Label>
                          <Input
                            data-testid="estimation-email-input"
                            type="email"
                            value={estimationEmail}
                            onChange={(e) => setEstimationEmail(e.target.value)}
                            placeholder="votre@email.com"
                            className="text-lg py-6"
                          />
                        </div>
                        <div>
                          <Label className="text-navy font-semibold text-sm mb-2 block">Téléphone <span className="text-muted-foreground font-normal">(facultatif)</span></Label>
                          <Input
                            data-testid="estimation-phone-input"
                            type="tel"
                            value={formData.phone || ''}
                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                            placeholder="06 12 34 56 78"
                            className="text-lg py-6"
                          />
                        </div>
                      </div>
                      
                      <p className="text-xs text-muted-foreground mt-4 text-center">
                        Vos données sont sécurisées et ne seront jamais partagées
                      </p>
                    </CardContent>
                  </Card>

                  <div className="flex justify-between flex-wrap gap-2">
                    <Button variant="outline" onClick={() => setEstimationStep(2)}>
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Retour
                    </Button>
                    <Button
                      data-testid="estimation-next-3"
                      onClick={async () => {
                        try {
                          await axios.post(`${API}/prospect/capture`, {
                            email: estimationEmail,
                            phone: formData.phone,
                            address: formData.address,
                            property_type: propertyType,
                            source: 'estimation',
                            ...(partnerSubdomain && { partner_subdomain: partnerSubdomain })
                          });
                        } catch (e) { console.error('Capture error:', e); }
                        setEstimationStep(4);
                        getEstimation();
                      }}
                      disabled={!estimationEmail || estimationLoading}
                      className="bg-gold hover:bg-gold/90 text-black px-8 py-6 text-lg font-semibold"
                    >
                      {estimationLoading ? (
                        <>Analyse en cours...</>
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          Recevoir mon estimation
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}

              {/* Estimation Step 4: Résultat */}
              {estimationStep === 4 && (
                <div className="space-y-6">
                  {!estimationResult ? (
                    <div className="text-center py-12">
                      <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 mb-4 animate-pulse">
                        <TrendingUp className="w-10 h-10 text-white" />
                      </div>
                      <h2 className="font-heading text-2xl md:text-3xl font-bold text-navy">Analyse en cours...</h2>
                      <p className="text-muted-foreground mt-2">Nous analysons les ventes réelles de votre quartier</p>
                    </div>
                  ) : (
                    <>
                      {/* Estimation Result */}
                      <div className="text-center mb-8">
                        <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-100 mb-4">
                          <CheckCircle className="w-10 h-10 text-emerald-600" />
                        </div>
                        <h2 className="font-heading text-2xl md:text-3xl font-bold text-navy">Votre estimation</h2>
                        <p className="text-muted-foreground mt-2">{formData.address}</p>
                      </div>

                      <Card className="border-2 border-emerald-500 shadow-xl">
                        <CardContent className="p-8">
                          <div className="grid md:grid-cols-2 gap-6">
                            <div className="text-center p-6 bg-emerald-50 rounded-xl">
                              <p className="text-sm text-muted-foreground mb-2">Prix moyen au m²</p>
                              <p className="text-4xl font-bold text-emerald-600">
                                {estimationResult.price_per_m2?.toLocaleString('fr-FR')} €
                              </p>
                              <p className="text-xs text-muted-foreground mt-2">
                                Basé sur {estimationResult.nb_transactions || 0} ventes DVF
                              </p>
                            </div>
                            <div className="text-center p-6 bg-gold/10 rounded-xl">
                              <p className="text-sm text-muted-foreground mb-2">Estimation totale</p>
                              <p className="text-4xl font-bold text-gold">
                                {estimationResult.estimated_price?.toLocaleString('fr-FR')} €
                              </p>
                              <p className="text-xs text-muted-foreground mt-2">
                                Pour {formData.surface} m²
                              </p>
                            </div>
                          </div>

                          {/* Features selected summary */}
                          {(selectedFeatures.length > 0 || vueSub) && (
                            <div className="mt-4 p-4 bg-gray-50 rounded-lg border">
                              <p className="text-sm font-semibold text-navy mb-2">Caractéristiques prises en compte :</p>
                              <div className="flex flex-wrap gap-1.5">
                                {selectedFeatures.map(fId => {
                                  const feature = (PROPERTY_FEATURES[propertyType] || []).find(f => f.id === fId);
                                  if (!feature) return null;
                                  return (
                                    <span key={fId} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-700">
                                      {feature.icon} {feature.label}
                                    </span>
                                  );
                                })}
                                {vueSub && (
                                  <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-emerald-100 text-emerald-700">
                                    🏔️ Vue : {vueSub.label}
                                  </span>
                                )}
                              </div>
                            </div>
                          )}

                          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                            <p className="text-sm text-blue-800 flex items-start gap-2">
                              <Award className="w-4 h-4 flex-shrink-0 mt-0.5 text-gold" />
                              <span>
                                <strong>Conseil de l'Expert :</strong> Cette estimation se base sur les données officielles DVF{selectedFeatures.length > 0 ? `, ajustée selon les caractéristiques de votre bien` : ''}. 
                                Pour une <span className="font-semibold">analyse technique complète</span> (état du bien, travaux, rentabilité réelle), 
                                un audit professionnel est recommandé.
                              </span>
                            </p>
                          </div>
                        </CardContent>
                      </Card>

                      {/* DVF Recent Transactions Table */}
                      {estimationResult.comparable_transactions && estimationResult.comparable_transactions.length > 0 && (
                        <Card className="border border-navy/20 shadow-lg" data-testid="dvf-transactions-card">
                          <CardContent className="p-4 md:p-6">
                            <div className="flex items-center gap-2 mb-4">
                              <div className="w-8 h-8 rounded-full bg-navy/10 flex items-center justify-center">
                                <BarChart3 className="w-4 h-4 text-navy" />
                              </div>
                              <div>
                                <h3 className="font-heading font-bold text-navy">Ventes récentes du quartier</h3>
                                <p className="text-xs text-muted-foreground">
                                  {estimationResult.comparable_transactions.length} ventes officielles DVF dans un rayon de 500m
                                </p>
                              </div>
                            </div>
                            <div className="overflow-x-auto -mx-4 md:mx-0">
                              <table className="w-full text-sm">
                                <thead className="bg-navy text-white">
                                  <tr>
                                    <th className="px-3 py-2 text-left font-semibold text-xs">Date</th>
                                    <th className="px-3 py-2 text-left font-semibold text-xs">Type</th>
                                    <th className="px-3 py-2 text-left font-semibold text-xs hidden md:table-cell">Adresse</th>
                                    <th className="px-3 py-2 text-center font-semibold text-xs">Surface</th>
                                    <th className="px-3 py-2 text-right font-semibold text-xs">Prix</th>
                                    <th className="px-3 py-2 text-right font-semibold text-xs">€/m²</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {estimationResult.comparable_transactions.map((tx, idx) => (
                                    <tr
                                      key={idx}
                                      className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}
                                      data-testid={`dvf-tx-row-${idx}`}
                                    >
                                      <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">
                                        {tx.date ? new Date(tx.date).toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' }) : '-'}
                                      </td>
                                      <td className="px-3 py-2 text-xs">
                                        <div className="font-medium text-navy">{tx.type_local || '-'}</div>
                                        {tx.nb_pieces && (
                                          <div className="text-muted-foreground text-[10px]">{tx.nb_pieces} pièce{parseInt(tx.nb_pieces) > 1 ? 's' : ''}</div>
                                        )}
                                      </td>
                                      <td className="px-3 py-2 text-xs hidden md:table-cell">
                                        <div className="text-navy truncate max-w-[180px]">{tx.adresse || tx.commune || '-'}</div>
                                        {tx.code_postal && (
                                          <div className="text-muted-foreground text-[10px]">{tx.code_postal} {tx.commune}</div>
                                        )}
                                      </td>
                                      <td className="px-3 py-2 text-xs text-center text-navy">{tx.surface}m²</td>
                                      <td className="px-3 py-2 text-xs text-right font-semibold text-navy whitespace-nowrap">
                                        {tx.valeur?.toLocaleString('fr-FR')} €
                                      </td>
                                      <td className="px-3 py-2 text-xs text-right font-semibold text-emerald-700 whitespace-nowrap">
                                        {tx.prix_m2?.toLocaleString('fr-FR')} €
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                            <p className="text-[10px] text-muted-foreground mt-3 italic">
                              Source : Données DVF officielles (Demandes de Valeurs Foncières, data.gouv.fr)
                            </p>
                          </CardContent>
                        </Card>
                      )}

                      <div className="text-center space-y-4">
                        <p className="text-sm text-muted-foreground">
                          Un email avec votre estimation a été envoyé à <strong>{estimationEmail}</strong>
                        </p>
                        
                        <Button
                          data-testid="go-to-audit-btn"
                          onClick={() => {
                            switchMode('audit');
                            setStep(1);
                          }}
                          className="bg-navy hover:bg-navy/90 text-white w-full text-sm sm:text-lg px-4 sm:px-8 py-4 sm:py-6"
                        >
                          <Target className="w-4 h-4 sm:w-5 sm:h-5 mr-2 shrink-0" />
                          <span>Vérifier la faisabilité technique (Audit complet)</span>
                        </Button>
                        
                        <p className="text-xs text-muted-foreground">
                          L'audit complet inclut : analyse des travaux, calcul de rentabilité, rapport bancaire
                        </p>
                      </div>
                    </>
                  )}
                </div>
              )}
            </>
          )}

          {/* ==================== CHANTIER VIP MODE ==================== */}
          {mode === 'chantier' && (
            <div className="space-y-6">
              {/* Choice Screen - Select between Suivi Travaux or Conseil Stratégique */}
              {!vipSubMode && !chantierSubmitted && !conseilSubmitted && (
                <div className="space-y-6">
                  <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gold/20 mb-4">
                      <Shield className="w-8 h-8 text-gold" />
                    </div>
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-navy">Accompagnement VIP</h2>
                    <p className="text-muted-foreground mt-2 max-w-lg mx-auto">
                      Choisissez le type d'accompagnement dont vous avez besoin
                    </p>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
                    {/* Option 1: Suivi de Travaux */}
                    <button
                      data-testid="vip-choice-travaux"
                      onClick={() => setVipSubMode('travaux')}
                      className="p-6 rounded-2xl border-2 border-gold/30 bg-white hover:border-gold hover:bg-gold/5 transition-all shadow-md hover:shadow-lg text-left"
                    >
                      <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center mb-4">
                        <Shield className="w-6 h-6 text-gold" />
                      </div>
                      <h3 className="font-heading text-xl font-bold text-navy mb-2">Suivi de Travaux</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Projet de rénovation ? Je vous accompagne de A à Z : devis, suivi chantier, réception.
                      </p>
                      <ul className="space-y-2 text-sm">
                        <li className="flex items-center gap-2 text-navy/80">
                          <Check className="w-4 h-4 text-emerald-600" />
                          +50 artisans partenaires
                        </li>
                        <li className="flex items-center gap-2 text-navy/80">
                          <Check className="w-4 h-4 text-emerald-600" />
                          Économies jusqu'à 20%
                        </li>
                        <li className="flex items-center gap-2 text-navy/80">
                          <Check className="w-4 h-4 text-emerald-600" />
                          Tous corps d'état
                        </li>
                      </ul>
                      <div className="mt-4 flex items-center gap-2 text-gold font-semibold">
                        Choisir <ArrowRight className="w-4 h-4" />
                      </div>
                    </button>

                    {/* Option 2: Conseil Stratégique */}
                    <button
                      data-testid="vip-choice-conseil"
                      onClick={() => setVipSubMode('conseil')}
                      className="p-6 rounded-2xl border-2 border-navy/30 bg-white hover:border-navy hover:bg-navy/5 transition-all shadow-md hover:shadow-lg text-left"
                    >
                      <div className="w-12 h-12 rounded-full bg-navy/10 flex items-center justify-center mb-4">
                        <Award className="w-6 h-6 text-navy" />
                      </div>
                      <h3 className="font-heading text-xl font-bold text-navy mb-2">Conseil Stratégique</h3>
                      <p className="text-sm text-muted-foreground mb-4">
                        Besoin d'un avis expert ? Session de conseil personnalisé pour vos projets immobiliers.
                      </p>
                      <ul className="space-y-2 text-sm">
                        <li className="flex items-center gap-2 text-navy/80">
                          <Check className="w-4 h-4 text-emerald-600" />
                          25 ans d'expérience
                        </li>
                        <li className="flex items-center gap-2 text-navy/80">
                          <Check className="w-4 h-4 text-emerald-600" />
                          Analyse personnalisée
                        </li>
                        <li className="flex items-center gap-2 text-navy/80">
                          <Check className="w-4 h-4 text-emerald-600" />
                          Accompagnement sur mesure
                        </li>
                      </ul>
                      <div className="mt-4 flex items-center gap-2 text-navy font-semibold">
                        Choisir <ArrowRight className="w-4 h-4" />
                      </div>
                    </button>
                  </div>
                </div>
              )}

              {/* Suivi de Travaux Form */}
              {vipSubMode === 'travaux' && !chantierSubmitted && (
                <>
                  <Button
                    variant="ghost"
                    onClick={() => setVipSubMode(null)}
                    className="mb-4"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" /> Retour aux choix
                  </Button>
                  
                  <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gold/20 mb-4">
                      <Shield className="w-8 h-8 text-gold" />
                    </div>
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-navy">Suivi de Travaux VIP</h2>
                    <p className="text-muted-foreground mt-2 max-w-lg mx-auto">
                      Projet de rénovation ? <span className="text-gold font-semibold">Ne restez pas seul.</span> Profitez de mes 25 ans de métier pour obtenir des devis fiables et un chantier livré sans stress.
                    </p>
                  </div>

                  {/* Pourquoi passer par moi ? - Network Benefits */}
                  <div className="bg-gradient-to-br from-emerald-50 to-gold/5 border-2 border-emerald-200 rounded-xl p-5 mb-6">
                    <h3 className="font-heading text-lg font-bold text-navy mb-4 flex items-center gap-2">
                      <Users className="w-5 h-5 text-emerald-600" />
                      Pourquoi passer par moi ?
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center">
                          <Check className="w-4 h-4 text-emerald-600" />
                        </div>
                        <p className="text-sm text-navy/80">
                          <span className="font-semibold text-emerald-700">Économisez jusqu'à 20%</span> sur vos matériaux grâce à mes remises professionnelles fournisseurs.
                        </p>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center">
                          <Check className="w-4 h-4 text-emerald-600" />
                        </div>
                        <p className="text-sm text-navy/80">
                          <span className="font-semibold text-emerald-700">Tous corps d'état :</span> Maçonnerie, Électricité, Plomberie, Menuiserie, Toiture, Isolation...
                        </p>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center">
                          <Check className="w-4 h-4 text-emerald-600" />
                        </div>
                        <p className="text-sm text-navy/80">
                          <span className="font-semibold text-emerald-700">+50 artisans partenaires</span> sélectionnés et suivis sur le terrain depuis 25 ans.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Expert Signature */}
                  <div className="bg-gradient-to-r from-gold/10 to-navy/5 border border-gold/30 rounded-xl p-4">
                    <p className="text-sm italic text-navy/80 leading-relaxed text-center">
                      "Je vous accompagne de A à Z : validation des devis, suivi de chantier, réception des travaux. Mon expertise terrain vous protège des malfaçons et dépassements de budget."
                    </p>
                    <p className="text-xs text-gold font-semibold mt-2 text-center">— L'Expert, 25 ans de métier</p>
                  </div>

                  <Card className="border-2 border-gold/30 shadow-lg">
                    <CardContent className="p-6 space-y-6">
                      {/* Address */}
                      <div>
                        <Label className="text-navy font-semibold mb-2 block">
                          Adresse du projet *
                        </Label>
                        <Input
                          data-testid="chantier-address-input"
                          value={chantierData.address}
                          onChange={(e) => setChantierData({ ...chantierData, address: e.target.value })}
                          placeholder="Ex: 45 Rue de la République, 69001 Lyon"
                          className="border-gold/30 focus:border-gold"
                        />
                      </div>

                      {/* Budget */}
                      <div>
                        <Label className="text-navy font-semibold mb-2 block">
                          Budget travaux estimé *
                        </Label>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {BUDGET_OPTIONS.map((option) => (
                            <button
                              key={option.value}
                              data-testid={`budget-${option.value}`}
                              onClick={() => setChantierData({ ...chantierData, budget: option.value })}
                              className={`p-3 rounded-xl border-2 transition-all text-center ${
                                chantierData.budget === option.value 
                                  ? 'border-gold bg-gold/10 ring-2 ring-gold' 
                                  : 'border-gray-200 hover:border-gold/50'
                              }`}
                            >
                              <span className="text-sm font-medium text-navy">{option.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Description (optional) */}
                      <div>
                        <Label className="text-navy font-semibold mb-2 block">
                          Décrivez votre projet (optionnel)
                        </Label>
                        <textarea
                          data-testid="chantier-description"
                          value={chantierData.description}
                          onChange={(e) => setChantierData({ ...chantierData, description: e.target.value })}
                          placeholder="Ex: Rénovation complète d'un appartement de 80m² - cuisine, salle de bain, électricité..."
                          className="w-full p-3 border border-gold/30 rounded-lg focus:border-gold focus:ring-1 focus:ring-gold min-h-[100px] resize-none"
                        />
                      </div>

                      {/* Contact Info */}
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-navy font-semibold mb-2 block">
                            Téléphone *
                          </Label>
                          <Input
                            data-testid="chantier-phone-input"
                            type="tel"
                            value={chantierData.phone}
                            onChange={(e) => setChantierData({ ...chantierData, phone: e.target.value })}
                            placeholder="06 12 34 56 78"
                            className="border-gold/30 focus:border-gold"
                          />
                        </div>
                        <div>
                          <Label className="text-navy font-semibold mb-2 block">
                            Email *
                          </Label>
                          <Input
                            data-testid="chantier-email-input"
                            type="email"
                            value={chantierData.email}
                            onChange={(e) => setChantierData({ ...chantierData, email: e.target.value })}
                            placeholder="votre@email.com"
                            className="border-gold/30 focus:border-gold"
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <div className="flex justify-center">
                    <Button
                      data-testid="chantier-submit-btn"
                      onClick={submitChantierRequest}
                      disabled={chantierLoading || !chantierData.address || !chantierData.budget || !chantierData.email}
                      className="bg-gold hover:bg-gold/90 text-black px-8 py-6 text-lg font-semibold"
                    >
                      {chantierLoading ? (
                        'Envoi en cours...'
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          Être rappelé sous 24h
                        </>
                      )}
                    </Button>
                  </div>

                  <p className="text-center text-xs text-muted-foreground">
                    🔒 Vos données sont confidentielles. Rappel gratuit et sans engagement.
                  </p>
                </>
              )}

              {/* Conseil Stratégique Form - Full Content */}
              {vipSubMode === 'conseil' && !conseilSubmitted && (
                <>
                  <div className="flex items-center justify-between mb-4">
                    <Button
                      variant="ghost"
                      onClick={() => setVipSubMode(null)}
                    >
                      <ArrowLeft className="w-4 h-4 mr-2" /> Retour aux choix
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setShowConseilSuiviModal(true)}
                      data-testid="follow-conseil-btn"
                      className="border-navy/40 text-navy"
                    >
                      Suivre ma demande
                    </Button>
                  </div>
                  
                  {/* Header Section */}
                  <div className="text-center mb-8">
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-navy/10 mb-4">
                      <Award className="w-8 h-8 text-navy" />
                    </div>
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-navy">
                      Ne signez rien sans l'œil d'un expert.
                    </h2>
                    <p className="text-muted-foreground mt-3 max-w-lg mx-auto">
                      25 ans de terrain pour sécuriser vos investissements et vos chantiers.
                      <br />
                      <span className="text-gold font-bold">Un conseil à 99€ peut vous en faire économiser 10 000€.</span>
                    </p>
                    <Badge className="mt-3 bg-red-100 text-red-700 animate-pulse">
                      🔴 Créneaux limités
                    </Badge>
                  </div>

                  {/* 4 Atouts Majeurs */}
                  <div className="grid md:grid-cols-2 gap-4 mb-6">
                    {/* Audit de Devis */}
                    <div 
                      className={`p-5 rounded-xl border-2 cursor-pointer transition-all ${
                        conseilData.subject === 'audit_devis' 
                          ? 'border-navy bg-navy/5 ring-2 ring-navy' 
                          : 'border-gray-200 hover:border-navy/50'
                      }`}
                      onClick={() => setConseilData({...conseilData, subject: 'audit_devis'})}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                          <Shield className="w-5 h-5 text-emerald-600" />
                        </div>
                        <div>
                          <h4 className="font-bold text-navy">Audit de Devis "Anti-Arnaque"</h4>
                          <p className="text-sm text-muted-foreground mt-1">
                            J'analyse vos devis artisans. Je repère les oublis techniques, les surfacturations et je vous donne les vrais prix du marché.
                          </p>
                          {conseilData.subject === 'audit_devis' && (
                            <p className="text-xs text-emerald-600 mt-2 italic">"J'ai économisé 8 500€ sur mon devis cuisine grâce à son analyse."</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Validation de Faisabilité */}
                    <div 
                      className={`p-5 rounded-xl border-2 cursor-pointer transition-all ${
                        conseilData.subject === 'faisabilite' 
                          ? 'border-navy bg-navy/5 ring-2 ring-navy' 
                          : 'border-gray-200 hover:border-navy/50'
                      }`}
                      onClick={() => setConseilData({...conseilData, subject: 'faisabilite'})}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                          <Target className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <h4 className="font-bold text-navy">Validation de Faisabilité</h4>
                          <p className="text-sm text-muted-foreground mt-1">
                            Vous voulez casser un mur ? Aménager des combles ? Je vous dis si c'est possible techniquement et si le budget est réaliste.
                          </p>
                          {conseilData.subject === 'faisabilite' && (
                            <p className="text-xs text-blue-600 mt-2 italic">"Il m'a évité un projet à 40k€ qui n'était pas viable."</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Médiation de Conflit */}
                    <div 
                      className={`p-5 rounded-xl border-2 cursor-pointer transition-all ${
                        conseilData.subject === 'mediation' 
                          ? 'border-navy bg-navy/5 ring-2 ring-navy' 
                          : 'border-gray-200 hover:border-navy/50'
                      }`}
                      onClick={() => setConseilData({...conseilData, subject: 'mediation'})}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                          <Users className="w-5 h-5 text-orange-600" />
                        </div>
                        <div>
                          <h4 className="font-bold text-navy">Médiation de Conflit</h4>
                          <p className="text-sm text-muted-foreground mt-1">
                            Un artisan ne vient plus ? Le travail est mal fait ? Je vous donne les arguments techniques pour faire valoir vos droits et débloquer le chantier.
                          </p>
                          {conseilData.subject === 'mediation' && (
                            <p className="text-xs text-orange-600 mt-2 italic">"Grâce à lui, l'artisan a repris les travaux sous 48h."</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Stratégie MDB */}
                    <div 
                      className={`p-5 rounded-xl border-2 cursor-pointer transition-all ${
                        conseilData.subject === 'strategie_mdb' 
                          ? 'border-navy bg-navy/5 ring-2 ring-navy' 
                          : 'border-gray-200 hover:border-navy/50'
                      }`}
                      onClick={() => setConseilData({...conseilData, subject: 'strategie_mdb'})}
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center flex-shrink-0">
                          <TrendingUp className="w-5 h-5 text-gold" />
                        </div>
                        <div>
                          <h4 className="font-bold text-navy">Stratégie Marchand de Biens</h4>
                          <p className="text-sm text-muted-foreground mt-1">
                            Conseil sur la division parcellaire, optimisation de l'espace et calcul de rentabilité réelle avant achat.
                          </p>
                          {conseilData.subject === 'strategie_mdb' && (
                            <p className="text-xs text-gold mt-2 italic">"Son analyse m'a permis de négocier 25k€ sur le prix d'achat."</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Optimisation MaPrimeRénov' & DPE */}
                    <div 
                      className={`p-5 rounded-xl border-2 cursor-pointer transition-all md:col-span-2 ${
                        conseilData.subject === 'optimisation_maprimerenov' 
                          ? 'border-navy bg-navy/5 ring-2 ring-navy' 
                          : 'border-gray-200 hover:border-navy/50'
                      }`}
                      onClick={() => setConseilData({...conseilData, subject: 'optimisation_maprimerenov'})}
                      data-testid="conseil-optimisation-maprimerenov-btn"
                    >
                      <div className="flex items-start gap-3">
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                          <Leaf className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <h4 className="font-bold text-navy">🌱 Optimisation MaPrimeRénov' & DPE</h4>
                          <p className="text-sm text-muted-foreground mt-1">
                            Vous voulez maximiser vos aides à la rénovation énergétique ? Je décrypte votre DPE, chiffre vos aides (MaPrimeRénov', éco-PTZ, CEE, primes locales) et vous dis comment éviter l'interdiction locative 2025-2034.
                          </p>
                          {conseilData.subject === 'optimisation_maprimerenov' && (
                            <p className="text-xs text-green-700 mt-2 italic">"Grâce à son analyse, j'ai obtenu 18 500€ d'aides que j'ignorais totalement."</p>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Ce que vous obtenez */}
                  <div className="bg-gradient-to-br from-emerald-50 to-gold/5 border-2 border-emerald-200 rounded-xl p-5 mb-6">
                    <h3 className="font-heading text-lg font-bold text-navy mb-4 flex items-center gap-2">
                      <Check className="w-5 h-5 text-emerald-600" />
                      Ce que vous obtenez
                    </h3>
                    <div className="space-y-3">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center">
                          <Check className="w-4 h-4 text-emerald-600" />
                        </div>
                        <p className="text-sm text-navy/80">
                          <span className="font-semibold text-emerald-700">Un échange direct de 45min à 1h</span> (Visio ou téléphone)
                        </p>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center">
                          <Check className="w-4 h-4 text-emerald-600" />
                        </div>
                        <p className="text-sm text-navy/80">
                          <span className="font-semibold text-emerald-700">Une réponse claire et sans jargon</span> sur votre problématique
                        </p>
                      </div>
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center">
                          <Check className="w-4 h-4 text-emerald-600" />
                        </div>
                        <p className="text-sm text-navy/80">
                          <span className="font-semibold text-emerald-700">Une feuille de route précise</span> pour vos prochaines étapes
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Preuve Sociale */}
                  <div className="flex items-center justify-center gap-8 mb-6 py-4">
                    <div className="text-center">
                      <p className="text-2xl font-bold text-navy">+150</p>
                      <p className="text-xs text-muted-foreground">consultations</p>
                    </div>
                    <div className="h-10 w-px bg-gray-200"></div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-gold">98%</p>
                      <p className="text-xs text-muted-foreground">satisfaits</p>
                    </div>
                    <div className="h-10 w-px bg-gray-200"></div>
                    <div className="text-center">
                      <p className="text-2xl font-bold text-emerald-600">25 ans</p>
                      <p className="text-xs text-muted-foreground">d'expérience</p>
                    </div>
                  </div>

                  {/* Formulaire */}
                  <Card className="border-2 border-navy/30 shadow-lg">
                    <CardContent className="p-6 space-y-5">
                      <div className="text-center pb-2">
                        <p className="text-3xl font-bold text-navy">99€ <span className="text-lg font-normal text-muted-foreground">TTC</span></p>
                        <p className="text-sm text-emerald-600">✅ Garantie "Satisfait ou re-consulté"</p>
                      </div>

                      {!conseilData.subject && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
                          <p className="text-sm text-yellow-800">👆 Sélectionnez d'abord le type de conseil souhaité ci-dessus</p>
                        </div>
                      )}

                      {/* Contact Info */}
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label className="text-navy font-semibold mb-2 block">
                            Téléphone *
                          </Label>
                          <Input
                            data-testid="conseil-phone-input"
                            type="tel"
                            value={conseilData.phone}
                            onChange={(e) => setConseilData({ ...conseilData, phone: e.target.value })}
                            placeholder="06 12 34 56 78"
                            className="border-navy/30 focus:border-navy"
                          />
                        </div>
                        <div>
                          <Label className="text-navy font-semibold mb-2 block">
                            Email *
                          </Label>
                          <Input
                            data-testid="conseil-email-input"
                            type="email"
                            value={conseilData.email}
                            onChange={(e) => setConseilData({ ...conseilData, email: e.target.value })}
                            placeholder="votre@email.com"
                            className="border-navy/30 focus:border-navy"
                          />
                        </div>
                      </div>

                      {/* Description */}
                      <div>
                        <Label className="text-navy font-semibold mb-2 block">
                          Décrivez votre problème en 2 phrases *
                        </Label>
                        <textarea
                          data-testid="conseil-description"
                          value={conseilData.description}
                          onChange={(e) => setConseilData({ ...conseilData, description: e.target.value })}
                          placeholder="Ex: J'ai reçu un devis de 35 000€ pour refaire ma toiture. Je ne sais pas si c'est le bon prix..."
                          className="w-full p-3 border border-navy/30 rounded-lg focus:border-navy focus:ring-1 focus:ring-navy min-h-[100px] resize-none"
                        />
                        <p className="text-xs text-muted-foreground mt-1">Pour que je prépare l'appel efficacement</p>
                      </div>

                      {/* Upload Documents */}
                      <div>
                        <Label className="text-navy font-semibold mb-2 block">
                          Documents à joindre (optionnel)
                        </Label>
                        <div className="border-2 border-dashed border-navy/30 rounded-lg p-4 text-center hover:border-navy/50 transition-colors">
                          <input
                            type="file"
                            id="conseil-files"
                            multiple
                            accept="image/*,.pdf,.doc,.docx,.xls,.xlsx"
                            onChange={(e) => {
                              const files = Array.from(e.target.files || []);
                              setConseilFiles(prev => [...prev, ...files]);
                            }}
                            className="hidden"
                          />
                          <label htmlFor="conseil-files" className="cursor-pointer">
                            <Upload className="w-8 h-8 mx-auto text-navy/40 mb-2" />
                            <p className="text-sm text-navy/70 font-medium">
                              Cliquez pour ajouter des documents
                            </p>
                            <p className="text-xs text-muted-foreground mt-1">
                              Devis, photos, plans... (PDF, images, Word, Excel)
                            </p>
                          </label>
                        </div>
                        {conseilFiles.length > 0 && (
                          <div className="mt-3 space-y-2">
                            {conseilFiles.map((file, idx) => (
                              <div key={idx} className="flex items-center justify-between bg-gray-50 rounded-lg p-2">
                                <div className="flex items-center gap-2">
                                  <FileText className="w-4 h-4 text-navy/60" />
                                  <span className="text-sm text-navy truncate max-w-[200px]">{file.name}</span>
                                  <span className="text-xs text-muted-foreground">
                                    ({(file.size / 1024).toFixed(0)} Ko)
                                  </span>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setConseilFiles(prev => prev.filter((_, i) => i !== idx))}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Submit Button */}
                      <Button
                        data-testid="conseil-submit-btn"
                        onClick={async () => {
                          if (!conseilData.subject || !conseilData.phone || !conseilData.email || !conseilData.description) {
                            toast.error('Veuillez remplir tous les champs et sélectionner un type de conseil');
                            return;
                          }
                          setConseilLoading(true);
                          try {
                            // First upload files if any
                            let uploadedFileIds = [];
                            if (conseilFiles.length > 0) {
                              const formData = new FormData();
                              conseilFiles.forEach((file) => {
                                formData.append('files', file);
                              });
                              formData.append('type', 'conseil');
                              
                              const uploadRes = await axios.post(`${API}/conseil/upload-documents`, formData, {
                                headers: { 'Content-Type': 'multipart/form-data' }
                              });
                              uploadedFileIds = uploadRes.data.file_ids || [];
                            }
                            
                            // Then create checkout session
                            const response = await axios.post(`${API}/conseil/create-checkout-session`, {
                              email: conseilData.email,
                              phone: conseilData.phone,
                              conseil_type: conseilData.subject,
                              description: conseilData.description,
                              file_ids: uploadedFileIds
                            });
                            // Redirect to Stripe checkout
                            window.location.href = response.data.checkout_url;
                          } catch (error) {
                            console.error('Conseil checkout error:', error);
                            toast.error(error.response?.data?.detail || 'Erreur lors de la création du paiement');
                            setConseilLoading(false);
                          }
                        }}
                        disabled={conseilLoading || !conseilData.subject || !conseilData.phone || !conseilData.email || !conseilData.description}
                        className="w-full bg-navy hover:bg-navy/90 text-white py-6 text-lg font-semibold"
                      >
                        {conseilLoading ? (
                          'Redirection vers le paiement...'
                        ) : (
                          <>
                            <CreditCard className="w-5 h-5 mr-2" />
                            Réserver mon conseil — 99€
                          </>
                        )}
                      </Button>
                    </CardContent>
                  </Card>

                  <p className="text-center text-xs text-muted-foreground mt-4">
                    🔒 Paiement sécurisé par Stripe. Après paiement, choisissez votre créneau sur Calendly.
                  </p>
                </>
              )}

              {/* Conseil Success Screen */}
              {conseilSubmitted && (
                <div className="text-center py-12">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-emerald-100 mb-6">
                    <CheckCircle className="w-10 h-10 text-emerald-600" />
                  </div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-navy mb-4">
                    Paiement confirmé !
                  </h2>
                  <p className="text-muted-foreground max-w-md mx-auto mb-6">
                    Vous allez recevoir un email avec le lien pour <span className="font-semibold text-navy">réserver votre créneau</span>.
                  </p>
                  <Button
                    variant="outline"
                    onClick={() => switchMode('estimation')}
                  >
                    Retour à l'accueil
                  </Button>
                </div>
              )}

              {/* Chantier Success Screen */}
              {chantierSubmitted && (
                /* Success Screen */
                <div className="text-center py-12">
                  <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gold/20 mb-6">
                    <CheckCircle className="w-10 h-10 text-gold" />
                  </div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-navy mb-4">
                    Demande envoyée !
                  </h2>
                  <p className="text-muted-foreground max-w-md mx-auto mb-6">
                    Merci pour votre confiance. <span className="font-semibold text-gold">Je vous rappelle sous 24h</span> pour discuter de votre projet et vous proposer un accompagnement sur mesure.
                  </p>
                  
                  <Card className="max-w-md mx-auto border-gold/30">
                    <CardContent className="p-6">
                      <h3 className="font-semibold text-navy mb-3">Récapitulatif de votre demande</h3>
                      <div className="text-left space-y-2 text-sm">
                        <p><span className="text-muted-foreground">Adresse :</span> {chantierData.address}</p>
                        <p><span className="text-muted-foreground">Budget :</span> {BUDGET_OPTIONS.find(b => b.value === chantierData.budget)?.label}</p>
                        <p><span className="text-muted-foreground">Téléphone :</span> {chantierData.phone}</p>
                      </div>
                    </CardContent>
                  </Card>

                  <Button
                    variant="outline"
                    onClick={() => switchMode('estimation')}
                    className="mt-6"
                  >
                    Retour à l'accueil
                  </Button>
                </div>
              )}
              
              {/* Conseil Suivi Modal */}
              {showConseilSuiviModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="conseil-suivi-modal">
                  <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl">
                    <h3 className="font-heading text-xl font-bold text-navy mb-2">Suivre ma demande</h3>
                    <p className="text-sm text-muted-foreground mb-5">Saisissez votre email et votre code de suivi reçu par email (ex: CON-2026-00012).</p>
                    <div className="space-y-3">
                      <div>
                        <Label className="text-navy font-semibold mb-1 block">Email</Label>
                        <Input
                          type="email"
                          value={conseilSuiviEmail}
                          onChange={(e) => setConseilSuiviEmail(e.target.value)}
                          placeholder="votre@email.com"
                          data-testid="conseil-suivi-email-input"
                        />
                      </div>
                      <div>
                        <Label className="text-navy font-semibold mb-1 block">Code de suivi</Label>
                        <Input
                          type="text"
                          value={conseilSuiviCode}
                          onChange={(e) => setConseilSuiviCode(e.target.value.toUpperCase())}
                          placeholder="CON-2026-00001"
                          className="font-mono"
                          data-testid="conseil-suivi-code-input"
                        />
                      </div>
                    </div>
                    <div className="flex gap-2 mt-5">
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowConseilSuiviModal(false);
                          setConseilSuiviEmail('');
                          setConseilSuiviCode('');
                        }}
                        className="flex-1"
                        data-testid="conseil-suivi-cancel-btn"
                      >
                        Annuler
                      </Button>
                      <Button
                        onClick={async () => {
                          if (!conseilSuiviEmail || !conseilSuiviCode) {
                            toast.error('Renseignez l\'email et le code');
                            return;
                          }
                          setConseilSuiviLoading(true);
                          try {
                            const res = await axios.post(`${API}/conseil/lookup`, {
                              email: conseilSuiviEmail,
                              tracking_code: conseilSuiviCode
                            });
                            const cid = res.data?.conseil_id;
                            const code = res.data?.tracking_code || conseilSuiviCode;
                            if (cid) {
                              window.location.href = `/conseil/suivi?id=${cid}&email=${encodeURIComponent(conseilSuiviEmail)}&code=${code}`;
                            }
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Dossier introuvable');
                          } finally {
                            setConseilSuiviLoading(false);
                          }
                        }}
                        disabled={conseilSuiviLoading}
                        className="flex-1 bg-navy hover:bg-navy/90 text-white"
                        data-testid="conseil-suivi-submit-btn"
                      >
                        {conseilSuiviLoading ? 'Chargement...' : 'Accéder au dossier'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ==================== AUDIT MODE (Full Tunnel) ==================== */}
          {mode === 'audit' && (
            <>
          {/* Step 1: Location */}
          {step === 1 && (
            <div className="space-y-6">
              {/* Expert Signature Section */}
              <ExpertSignature />
              
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-navy/10 mb-4">
                  <MapPin className="w-8 h-8 text-navy" />
                </div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-navy">Localisation du bien</h2>
                <p className="text-muted-foreground mt-2 max-w-md mx-auto">
                  Commencez l'analyse de votre projet pour obtenir votre <span className="text-gold font-semibold">rapport de rentabilité bancaire</span>
                </p>
              </div>

              {/* Value Proposition Cards */}
              <div className="grid grid-cols-3 gap-4 mb-8">
                <div className="text-center p-4 bg-white rounded-xl border shadow-sm">
                  <Shield className="w-6 h-6 text-navy mx-auto mb-2" />
                  <p className="text-xs font-medium text-navy">Expertise</p>
                  <p className="text-xs text-muted-foreground">25 ans de métier</p>
                </div>
                <div className="text-center p-4 bg-white rounded-xl border shadow-sm">
                  <Clock className="w-6 h-6 text-gold mx-auto mb-2" />
                  <p className="text-xs font-medium text-navy">Rapide</p>
                  <p className="text-xs text-muted-foreground">Analyse en 12h</p>
                </div>
                <div className="text-center p-4 bg-white rounded-xl border shadow-sm">
                  <Target className="w-6 h-6 text-emerald-500 mx-auto mb-2" />
                  <p className="text-xs font-medium text-navy">Précis</p>
                  <p className="text-xs text-muted-foreground">Rapport bancaire</p>
                </div>
              </div>
              
              <Card className="border-2 border-navy/10 shadow-lg">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <Label htmlFor="address" className="text-navy font-semibold text-base">Adresse complète du bien</Label>
                    <Button
                      data-testid="geolocation-btn"
                      variant="outline"
                      size="sm"
                      onClick={handleGeolocation}
                      disabled={geolocating}
                      className="border-gold text-gold hover:bg-gold hover:text-black"
                    >
                      <MapPin className="w-4 h-4 mr-1" />
                      {geolocating ? 'Recherche...' : 'Géolocaliser'}
                    </Button>
                  </div>
                  
                  <div className="relative">
                    <AddressInput
                      value={formData.address}
                      onChange={(val) => setFormData(prev => ({ ...prev, address: val }))}
                      onSelect={selectAddress}
                      placeholder="12 Rue de la Paix, 75002 Paris"
                      className="pl-3 h-14 text-lg w-full border-2 rounded-lg focus:border-amber-400 focus:ring-2 focus:ring-amber-200 outline-none"
                      testId="address-input"
                    />
                  </div>
                  
                  <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
                    <span className="inline-block w-2 h-2 rounded-full bg-emerald-500"></span>
                    Autocomplétion réelle • API BAN (Base Adresse Nationale) - Gratuit
                  </div>

                  {/* Avertissement si l'adresse n'a pas de numéro de rue (lotissement, résidence, zone rurale) */}
                  {formData.address && !/^\s*\d/.test(formData.address) && (
                    <div className="mt-3 p-3 bg-amber-50 border border-amber-300 rounded-lg" data-testid="address-no-number-warning">
                      <p className="text-xs text-amber-900">
                        <span className="font-bold">⚠️ Adresse sans numéro de rue détectée</span> — courant pour les lotissements, résidences fermées ou zones rurales. La géolocalisation reste précise (votre point sur la carte est exact), mais pour vos documents administratifs (compromis, MaPrimeRénov'), précisez votre Lot / Bât / Pavillon ci-dessous.
                      </p>
                    </div>
                  )}

                  {/* Champ "Précision adresse" toujours disponible */}
                  <div className="mt-3">
                    <Label className="text-navy font-semibold text-sm">
                      Précision adresse <span className="text-xs font-normal text-muted-foreground">(optionnel) — Lot, Bâtiment, Étage, Pavillon, etc.</span>
                    </Label>
                    <input
                      data-testid="address-precision-input"
                      type="text"
                      value={formData.address_precision}
                      onChange={(e) => setFormData({ ...formData, address_precision: e.target.value })}
                      placeholder="ex: Lot 14, Bât B, 3ᵉ étage porte gauche…"
                      className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-amber-400 focus:outline-none text-sm"
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Cette précision sera ajoutée à votre adresse dans le PDF d'audit et les emails de suivi.
                    </p>
                  </div>
                  
                  {/* Map display when coordinates are available */}
                  {formData.latitude && formData.longitude && (
                    <div className="mt-4 space-y-3">
                      {/* Coordinates display */}
                      <div className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                        <div className="flex items-center gap-2">
                          <Navigation className="w-4 h-4 text-emerald-600" />
                          <span className="text-sm text-emerald-800">
                            <strong>Coordonnées GPS :</strong> {formData.latitude.toFixed(6)}, {formData.longitude.toFixed(6)}
                          </span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowMap(!showMap)}
                          className="text-emerald-600 hover:text-emerald-800 hover:bg-emerald-100"
                        >
                          {showMap ? 'Masquer la carte' : 'Voir sur la carte'}
                        </Button>
                      </div>
                      
                      {/* Interactive Map */}
                      {showMap && (
                        <div className="relative rounded-lg overflow-hidden border-2 border-navy/20 shadow-lg">
                          <div className="absolute top-2 right-2 z-[1000]">
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => setShowMap(false)}
                              className="bg-white/90 hover:bg-white shadow"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                          <div className="h-[300px]">
                            <MapContainer
                              center={[formData.latitude, formData.longitude]}
                              zoom={17}
                              style={{ height: '100%', width: '100%' }}
                              scrollWheelZoom={true}
                            >
                              <TileLayer
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                              />
                              <LocationMarker
                                position={[formData.latitude, formData.longitude]}
                                onPositionChange={handleMapPositionChange}
                              />
                            </MapContainer>
                          </div>
                          <div className="p-2 bg-navy/5 text-center text-xs text-muted-foreground">
                            <MapPin className="w-3 h-3 inline mr-1" />
                            Cliquez sur la carte pour ajuster la position du bien
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Trust indicators */}
              <div className="text-center pt-4">
                <p className="text-sm text-muted-foreground">
                  Rejoignez les <span className="font-semibold text-navy">+200 marchands de biens</span> qui sécurisent leurs investissements
                </p>
              </div>
            </div>
          )}

          {/* Step 2: Sale Type */}
          {step === 2 && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-navy/10 mb-4">
                  <Building2 className="w-8 h-8 text-navy" />
                </div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-navy">Stratégie Fiscale</h2>
                <p className="text-muted-foreground mt-2">Sélectionnez votre profil d'acquisition pour optimiser les frais</p>
              </div>
              
              <div className="grid gap-4">
                {SALE_TYPES.map((type) => {
                  const Icon = type.icon;
                  const isSelected = formData.sale_type_id === type.id;
                  return (
                    <button
                      key={type.id}
                      data-testid={`sale-type-${type.id}`}
                      onClick={() => {
                        if (partnerSubdomain && type.id !== 'particulier') {
                          setShowPartnerLockedModal(true);
                          return;
                        }
                        setFormData({
                          ...formData,
                          sale_type_id: type.id,
                          sale_type: type.sale_type,
                          fiscal_profile: type.fiscal_profile,
                        });
                      }}
                      className={`p-6 rounded-xl border-2 text-left transition-all ${
                        isSelected
                          ? 'border-gold bg-gold/5 shadow-lg ring-2 ring-gold/20'
                          : partnerSubdomain && type.id !== 'particulier'
                            ? 'border-gray-200 bg-gray-50 opacity-70 cursor-pointer'
                            : 'border-gray-200 hover:border-navy/30 bg-white hover:shadow-md'
                      }`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`p-3 rounded-lg ${isSelected ? 'bg-gold text-white' : 'bg-gray-100 text-navy'}`}>
                          <Icon className="w-6 h-6" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <h3 className="font-heading font-bold text-lg text-navy">{type.label}</h3>
                            <div className="flex items-center gap-2 flex-shrink-0">
                              {partnerSubdomain && type.id !== 'particulier' && (
                                <Badge className="text-[10px] bg-slate-100 text-slate-600 border border-slate-200">Payant</Badge>
                              )}
                              <Badge className={`font-mono font-bold text-lg ${isSelected ? 'bg-gold text-black' : 'bg-navy/10 text-navy'}`}>
                                {type.rate}
                              </Badge>
                            </div>
                          </div>
                          <p className="text-sm font-medium text-navy/80 mt-1">{type.description}</p>
                          <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{type.details}</p>
                        </div>
                        {isSelected && (
                          <CheckCircle className="w-6 h-6 text-gold flex-shrink-0" />
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-6">
                <p className="text-sm text-amber-800">
                  <strong>💡 Les taux exacts</strong> seront révélés dans votre <strong>Audit Complet</strong> avec les conseils personnalisés de l'expert.
                </p>
              </div>
            </div>
          )}

          {/* Step 3: Numbers */}
          {step === 3 && (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-navy/10 mb-4">
                  <span className="text-2xl font-bold text-navy">€</span>
                </div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-navy">Chiffres Clés</h2>
                <p className="text-muted-foreground mt-2">Renseignez les données financières du projet</p>
                <p className="text-xs text-muted-foreground mt-1 italic">💡 Section principale — utile pour investisseurs & acheteurs</p>
              </div>
              
              <Card className="border-2 border-navy/10 shadow-lg">
                <CardContent className="p-6 space-y-6">
                  <div>
                    <Label htmlFor="purchase_price" className="text-navy font-semibold flex items-center gap-2">
                      Prix d'Achat Net Vendeur
                      <Badge variant="outline" className="text-xs">Obligatoire</Badge>
                    </Label>
                    <div className="relative mt-2">
                      <Input
                        id="purchase_price"
                        data-testid="purchase-price-input"
                        type="number"
                        placeholder="150000"
                        value={formData.purchase_price}
                        onChange={(e) => setFormData({ ...formData, purchase_price: e.target.value })}
                        className="h-14 text-lg pr-12 border-2 focus:border-gold"
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground font-bold">€</span>
                    </div>
                  </div>
                  
                  <div>
                    <Label htmlFor="surface" className="text-navy font-semibold flex items-center gap-2">
                      Surface habitable
                      <Badge variant="outline" className="text-xs">Obligatoire</Badge>
                    </Label>
                    <div className="relative mt-2">
                      <Input
                        id="surface"
                        data-testid="surface-input"
                        type="number"
                        placeholder="45"
                        value={formData.surface}
                        onChange={(e) => setFormData({ ...formData, surface: e.target.value })}
                        className="h-14 text-lg pr-12 border-2 focus:border-gold"
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground font-bold">m²</span>
                    </div>
                  </div>
                  
                  {/* Surface terrain (audit) — visible si Maison/Local/Entrepot/Terrain */}
                  {['maison', 'local', 'entrepot', 'terrain'].includes(propertyType) && (
                    <div>
                      <Label htmlFor="land_surface" className="text-navy font-semibold flex items-center gap-2">
                        Surface du terrain
                        <Badge variant="secondary" className="text-xs">{propertyType === 'terrain' ? 'Obligatoire' : 'Recommandé'}</Badge>
                      </Label>
                      <p className="text-xs text-muted-foreground mt-1">
                        <strong>Surface totale de la parcelle cadastrale</strong> (comme sur le titre de propriété). Ex : parcelle 400 m² avec maison 100 m² dessus → tapez 400.
                      </p>
                      <div className="relative mt-2">
                        <Input
                          id="land_surface"
                          data-testid="land-surface-input"
                          type="number"
                          placeholder="250"
                          value={formData.land_surface}
                          onChange={(e) => setFormData({ ...formData, land_surface: e.target.value })}
                          className="h-14 text-lg pr-12 border-2 focus:border-gold"
                        />
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground font-bold">m²</span>
                      </div>
                    </div>
                  )}
                  

                </CardContent>
              </Card>

              {/* Quick calculation preview */}
              {formData.purchase_price && formData.surface && (
                <Card className="bg-navy text-white border-0">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-white/70">Prix au m²</span>
                      <span className="font-bold text-gold">
                        {Math.round(formData.purchase_price / formData.surface).toLocaleString('fr-FR')} €/m²
                      </span>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* === AUDIT FISCAL OPTIMISÉ (optionnel, pour calcul abattements PV) === */}
              {/* 🔒 MASQUÉ VISUELLEMENT (garde le code intact - à ré-afficher plus tard) */}
              {/* Pour ré-afficher : retire "hidden" de la Card ci-dessous OU dis à l'agent "réactive le bloc audit fiscal optimisé" */}
              <Card hidden className="border-2 border-amber-200 shadow-lg" data-testid="audit-fiscal-block">
                <CardContent className="p-6 space-y-5">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">🧾</div>
                    <div className="flex-1">
                      <h3 className="font-heading text-lg font-bold text-navy">Audit fiscal optimisé</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        <span className="font-semibold text-amber-700">Si vous possédez déjà ce bien (revente prévue par vous-même).</span> Si vous renseignez ces informations, l'expert calculera vos <b>abattements pour durée de détention</b> (jusqu'à -100% d'impôt sur la plus-value !). Sans ces données, l'audit appliquera le calcul standard (36,2% fixe). Vous pourrez compléter par email plus tard.
                      </p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-navy font-semibold text-sm">Année d'acquisition du bien</Label>
                      <input
                        data-testid="audit-purchase-year"
                        type="number"
                        min="1900" max={new Date().getFullYear()}
                        value={formData.purchase_year}
                        onChange={(e) => setFormData({ ...formData, purchase_year: e.target.value })}
                        placeholder="ex: 2015"
                        className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-amber-400 focus:outline-none"
                      />
                      <p className="text-xs text-muted-foreground mt-1">Année où le bien a été acheté (par vous ou ancien propriétaire si vous reprenez).</p>
                    </div>
                    <div>
                      <Label className="text-navy font-semibold text-sm">Prix d'achat historique (€)</Label>
                      <input
                        data-testid="audit-historical-price"
                        type="number"
                        min="0"
                        value={formData.historical_purchase_price}
                        onChange={(e) => setFormData({ ...formData, historical_purchase_price: e.target.value })}
                        placeholder="Si différent du prix actuel"
                        className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-amber-400 focus:outline-none"
                      />
                      <p className="text-xs text-muted-foreground mt-1">Si vous revendez un bien que vous possédiez déjà — sinon laissez vide.</p>
                    </div>
                    <div>
                      <Label className="text-navy font-semibold text-sm">Année de construction <span className="text-gray-400 text-xs">(optionnel)</span></Label>
                      <input
                        data-testid="audit-construction-year"
                        type="number"
                        min="1700" max={new Date().getFullYear()}
                        value={formData.construction_year}
                        onChange={(e) => setFormData({ ...formData, construction_year: e.target.value })}
                        placeholder="ex: 1985"
                        className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-amber-400 focus:outline-none"
                      />
                      <p className="text-xs text-muted-foreground mt-1">≥ 15 ans = éligible MaPrimeRénov'. &gt; 1948 = règles anciennes.</p>
                    </div>
                    <div>
                      <Label className="text-navy font-semibold text-sm">Classe DPE actuelle <span className="text-gray-400 text-xs">(optionnel)</span></Label>
                      <select
                        data-testid="audit-dpe-class"
                        value={formData.dpe_class}
                        onChange={(e) => setFormData({ ...formData, dpe_class: e.target.value })}
                        className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-lg focus:border-amber-400 focus:outline-none"
                      >
                        <option value="">Je ne sais pas</option>
                        <option value="A">A (Très performant - ≤ 70 kWh/m²/an)</option>
                        <option value="B">B (Performant - 71 à 110)</option>
                        <option value="C">C (Assez performant - 111 à 180)</option>
                        <option value="D">D (Moyen - 181 à 250)</option>
                        <option value="E">E (Passoire - Interdit location 2034)</option>
                        <option value="F">F (Passoire - Interdit location 2028)</option>
                        <option value="G">G (Passoire - Interdit location 2025)</option>
                      </select>
                      <p className="text-xs text-muted-foreground mt-1">💡 Permet d'analyser les alertes réglementaires (interdictions locatives)</p>
                    </div>
                    <div>
                      <Label className="text-navy font-semibold text-sm">Résidence principale ?</Label>
                      <div className="flex items-center gap-2 mt-2">
                        <input
                          data-testid="audit-rp-checkbox"
                          type="checkbox"
                          checked={formData.is_residence_principale}
                          onChange={(e) => setFormData({ ...formData, is_residence_principale: e.target.checked })}
                          className="w-5 h-5 accent-amber-500"
                        />
                        <span className="text-sm text-gray-700">Oui, j'occupe ce bien comme résidence principale</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">Si oui : exonération totale d'impôt sur la plus-value !</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* === ESTIMATION EXPERT (DVF + caractéristiques) — alimente le rapport PDF === */}
              <Card className="border-2 border-emerald-200 shadow-lg" data-testid="audit-estimation-block">
                <CardContent className="p-6 space-y-6">
                  <div className="flex items-start gap-3">
                    <div className="text-2xl">📊</div>
                    <div className="flex-1">
                      <h3 className="font-heading text-lg font-bold text-navy">Affinez l'estimation expert</h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        L'expert calculera la valeur réelle du bien (DVF + caractéristiques + valorisation travaux) et la comparera à votre estimation dans le rapport PDF final.
                      </p>
                    </div>
                  </div>

                  {/* Property Type */}
                  <div>
                    <Label className="text-navy font-semibold text-sm mb-3 block">
                      Type de bien
                    </Label>
                    <div className="grid grid-cols-3 md:grid-cols-4 gap-2">
                      {PROPERTY_TYPES.map((type) => (
                        <button
                          key={type.id}
                          data-testid={`audit-property-type-${type.id}`}
                          type="button"
                          onClick={() => { setPropertyType(type.id); setPropertySubType(''); setSelectedFeatures([]); setFeatureQuantities({}); }}
                          className={`p-2 rounded-lg border-2 transition-all text-center ${
                            propertyType === type.id
                              ? 'border-emerald-500 bg-emerald-50 ring-1 ring-emerald-500'
                              : 'border-gray-200 hover:border-emerald-300'
                          }`}
                        >
                          <span className="text-xl block mb-0.5">{type.icon}</span>
                          <span className="text-[10px] md:text-xs font-medium text-navy leading-tight block">{type.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Sub-type */}
                  {PROPERTY_SUBCATEGORIES[propertyType] && (
                    <div>
                      <Label className="text-navy font-semibold text-sm mb-3 block">
                        Précisez le type
                      </Label>
                      <div className="flex flex-wrap gap-2">
                        {PROPERTY_SUBCATEGORIES[propertyType].map((sub) => (
                          <button
                            key={sub.id}
                            data-testid={`audit-property-subtype-${sub.id}`}
                            type="button"
                            onClick={() => setPropertySubType(sub.id)}
                            className={`px-4 py-2 rounded-lg border-2 text-sm font-medium transition-all ${
                              propertySubType === sub.id
                                ? 'border-emerald-500 bg-emerald-50 text-emerald-700 ring-1 ring-emerald-500'
                                : 'border-gray-200 text-gray-600 hover:border-emerald-300 hover:bg-emerald-50/50'
                            }`}
                          >
                            {sub.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Features */}
                  {PROPERTY_FEATURES[propertyType] && (
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <Label className="text-navy font-semibold text-sm">
                          Caractéristiques du bien
                        </Label>
                        {selectedFeatures.length > 0 && (
                          <Badge className="bg-emerald-100 text-emerald-700 text-xs font-bold">
                            +{getTotalCoefficient()}% · {selectedFeatures.length} sélectionnée{selectedFeatures.length > 1 ? 's' : ''}
                          </Badge>
                        )}
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                        {PROPERTY_FEATURES[propertyType].map((feature) => {
                          const isSelected = selectedFeatures.includes(feature.id);
                          const isQuantifiable = feature.inputType === 'quantity';
                          const isSurface = feature.inputType === 'surface';

                          if (feature.inputType === 'vue_type') {
                            const currentCat = vueOptionsData.find(c => c.id === vueCategory);
                            return (
                              <div key={feature.id} className="col-span-2 md:col-span-3 flex flex-col">
                                <button data-testid="audit-feature-vue_type"
                                  type="button"
                                  onClick={() => setVueExpanded(!vueExpanded)}
                                  className={`flex items-center gap-2 px-3 py-2.5 border-2 text-sm transition-all text-left ${(vueExpanded || vueSub) ? 'rounded-t-lg rounded-b-none border-b-0' : 'rounded-lg'} ${vueSub ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'}`}>
                                  <span className="text-lg shrink-0">🏔️</span>
                                  <span className="flex-1 font-medium text-xs">
                                    {vueSub ? `Vue : ${vueSub.label} (+${vueSub.coef}%)` : 'Type de Vue'}
                                  </span>
                                  {vueSub
                                    ? <button type="button" onClick={(e) => { e.stopPropagation(); setVueSub(null); setVueCategory(null); setVueExpanded(false); }} className="text-xs px-1.5 py-0.5 rounded hover:bg-red-100 text-red-500">✕</button>
                                    : <span className="text-xs text-gray-400">{vueExpanded ? '▲' : '▼'}</span>
                                  }
                                </button>
                                {(vueExpanded || vueSub) && (
                                  <div className={`border-2 border-t-0 rounded-b-lg p-3 space-y-3 ${vueSub ? 'border-emerald-500 bg-emerald-50/50' : 'border-gray-200 bg-gray-50'}`}>
                                    <div>
                                      <p className="text-xs font-medium text-gray-500 mb-2">Catégorie</p>
                                      <div className="grid grid-cols-2 gap-1.5">
                                        {vueOptionsData.map(cat => (
                                          <button key={cat.id} type="button"
                                            onClick={() => { setVueCategory(cat.id); setVueSub(null); }}
                                            className={`flex flex-col items-start p-2 rounded-lg border text-xs transition-all ${vueCategory === cat.id ? 'border-emerald-500 bg-emerald-100' : 'border-gray-200 hover:border-gray-300 bg-white'}`}>
                                            <span className="font-medium">{cat.icon} {cat.label}</span>
                                            <span className="text-gray-400 text-[10px] mt-0.5 leading-tight">{cat.subtitle}</span>
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                    {currentCat && (
                                      <div>
                                        <p className="text-xs font-medium text-gray-500 mb-2">Précisez :</p>
                                        <div className="space-y-1">
                                          {currentCat.sub.map(sub => (
                                            <label key={sub.id}
                                              className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer border text-xs transition-all ${vueSub?.id === sub.id ? 'border-emerald-500 bg-emerald-100' : 'border-transparent hover:bg-gray-100'}`}>
                                              <input type="radio" name="vue_sub_audit" checked={vueSub?.id === sub.id}
                                                onChange={() => { setVueSub(sub); setVueExpanded(false); }}
                                                className="accent-emerald-500" />
                                              <span className="flex-1">{sub.label}</span>
                                            </label>
                                          ))}
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          }

                          return (
                            <div key={feature.id} className="flex flex-col">
                              <button
                                data-testid={`audit-feature-${feature.id}`}
                                type="button"
                                onClick={() => toggleFeature(feature.id)}
                                className={`flex items-center gap-2 px-3 py-2.5 rounded-lg border-2 text-sm transition-all text-left ${
                                  isSelected
                                    ? 'border-emerald-500 bg-emerald-50 text-emerald-700'
                                    : 'border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                                } ${isSelected && (isQuantifiable || isSurface) ? 'rounded-b-none border-b-0' : ''}`}
                              >
                                <span className="text-lg shrink-0">{feature.icon}</span>
                                <span className="flex-1 font-medium text-xs">{feature.label}</span>
                                {isSelected && <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />}
                              </button>
                              {isSelected && isQuantifiable && (
                                <div className="flex items-center justify-between border-2 border-t-0 border-emerald-500 bg-emerald-50 rounded-b-lg px-3 py-1.5">
                                  <button type="button" onClick={(e) => { e.stopPropagation(); updateFeatureQuantity(feature.id, (featureQuantities[feature.id] || 1) - 1); }} className="w-7 h-7 rounded-full bg-white border border-emerald-300 flex items-center justify-center text-emerald-700 font-bold text-sm hover:bg-emerald-100">-</button>
                                  <span className="text-sm font-bold text-emerald-700">{featureQuantities[feature.id] || 1}</span>
                                  <button type="button" onClick={(e) => { e.stopPropagation(); updateFeatureQuantity(feature.id, (featureQuantities[feature.id] || 1) + 1); }} className="w-7 h-7 rounded-full bg-white border border-emerald-300 flex items-center justify-center text-emerald-700 font-bold text-sm hover:bg-emerald-100">+</button>
                                </div>
                              )}
                              {isSelected && isSurface && (
                                <div className="flex items-center border-2 border-t-0 border-emerald-500 bg-emerald-50 rounded-b-lg px-3 py-1.5 gap-2">
                                  <input
                                    type="number"
                                    value={featureQuantities[feature.id] || ''}
                                    onChange={(e) => updateFeatureQuantity(feature.id, parseInt(e.target.value) || 0)}
                                    onClick={(e) => e.stopPropagation()}
                                    placeholder="m²"
                                    className="w-full text-center text-sm font-bold bg-white border border-emerald-300 rounded px-2 py-1 text-emerald-700 outline-none focus:ring-1 focus:ring-emerald-400"
                                  />
                                  <span className="text-xs text-emerald-600 font-medium whitespace-nowrap">m²</span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* Step 4: Media Upload (Videos, Documents, Photos) */}
          {step === 4 && (
            <div className="space-y-6">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-navy/10 mb-4">
                  <Camera className="w-8 h-8 text-navy" />
                </div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-navy">Pièces du Dossier</h2>
                <p className="text-muted-foreground mt-2">Ajoutez vos vidéos, documents et photos pour une analyse complète</p>
              </div>

              <div className="relative">
              {partnerSubdomain && (
                <div
                  className="absolute inset-0 z-10 cursor-pointer rounded-2xl"
                  onClick={() => setShowPartnerLockedModal(true)}
                  title="Fonctionnalité disponible dans l'Audit Personnalisé complet"
                />
              )}
              {/* Media Type Tabs */}
              <div className="flex bg-gray-100 rounded-xl p-1 gap-1">
                <button
                  data-testid="tab-videos"
                  onClick={() => setActiveMediaTab('videos')}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-medium transition-all ${
                    activeMediaTab === 'videos' 
                      ? 'bg-white text-navy shadow-sm' 
                      : 'text-muted-foreground hover:text-navy'
                  }`}
                >
                  <Video className="w-4 h-4" />
                  <span>Vidéos</span>
                  {Object.keys(formData.videos).length > 0 && (
                    <Badge className="bg-emerald-500 text-white text-xs">{Object.keys(formData.videos).length}</Badge>
                  )}
                </button>
                <button
                  data-testid="tab-documents"
                  onClick={() => setActiveMediaTab('documents')}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-medium transition-all ${
                    activeMediaTab === 'documents' 
                      ? 'bg-white text-navy shadow-sm' 
                      : 'text-muted-foreground hover:text-navy'
                  }`}
                >
                  <FileText className="w-4 h-4" />
                  <span>Documents</span>
                  {Object.keys(formData.documents).length > 0 && (
                    <Badge className="bg-emerald-500 text-white text-xs">{Object.keys(formData.documents).length}</Badge>
                  )}
                </button>
                <button
                  data-testid="tab-photos"
                  onClick={() => setActiveMediaTab('photos')}
                  className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg font-medium transition-all ${
                    activeMediaTab === 'photos' 
                      ? 'bg-white text-navy shadow-sm' 
                      : 'text-muted-foreground hover:text-navy'
                  }`}
                >
                  <Image className="w-4 h-4" />
                  <span>Photos</span>
                  {Object.values(formData.photos).flat().length > 0 && (
                    <Badge className="bg-emerald-500 text-white text-xs">{Object.values(formData.photos).flat().length}</Badge>
                  )}
                </button>
              </div>

              {/* Videos Tab */}
              {activeMediaTab === 'videos' && (
                <div className="space-y-4 animate-fade-in">
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                    <p className="text-sm text-emerald-800 flex items-start gap-2">
                      <Award className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>
                        <strong>Conseil d'expert :</strong> Les vidéos permettent de détecter des problèmes invisibles sur photos 
                        (humidité, bruits, état des joints...). Plus vos vidéos sont détaillées, plus mon analyse sera précise.
                        <strong className="text-emerald-700"> Ajoutez autant de vidéos que nécessaire !</strong>
                      </span>
                    </p>
                  </div>
                  
                  <div className="space-y-3">
                    {VIDEO_TYPES.map((video) => {
                      const videoData = formData.videos[video.id];
                      const isMultiple = video.multiple;
                      const hasVideo = isMultiple 
                        ? (Array.isArray(videoData) && videoData.length > 0) 
                        : !!videoData;
                      const videoCount = isMultiple && Array.isArray(videoData) ? videoData.length : (hasVideo ? 1 : 0);
                      
                      return (
                        <div
                          key={video.id}
                          className={`p-4 rounded-xl border-2 transition-all ${
                            hasVideo ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 bg-white hover:border-navy/30'
                          }`}
                        >
                          <div className="flex items-center gap-4">
                            <div className={`p-2 rounded-lg ${hasVideo ? 'bg-emerald-500 text-white' : 'bg-gray-100 text-navy'}`}>
                              {hasVideo ? <CheckCircle className="w-5 h-5" /> : <video.icon className="w-5 h-5" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-navy flex items-center gap-2">
                                {video.label}
                                {isMultiple && <Badge className="bg-gold/20 text-gold text-xs">Illimité</Badge>}
                              </h4>
                              <p className="text-xs text-muted-foreground truncate">{video.description}</p>
                              {hasVideo && (
                                <p className="text-xs text-emerald-600 font-medium mt-1">
                                  {isMultiple ? `${videoCount} vidéo(s) ajoutée(s)` : (videoData?.name || 'Vidéo ajoutée')}
                                </p>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <input
                                ref={(el) => fileInputRefs.current[video.id] = el}
                                type="file"
                                accept={video.id === 'commentaire' ? 'audio/*,video/*' : 'video/*'}
                                multiple={isMultiple}
                                onChange={(e) => handleVideoSelect(video.id, e.target.files, isMultiple)}
                                className="hidden"
                              />
                              <Button
                                data-testid={`video-upload-${video.id}`}
                                variant={hasVideo ? "outline" : "default"}
                                size="sm"
                                onClick={() => triggerFileInput(video.id)}
                                className={hasVideo 
                                  ? "border-emerald-500 text-emerald-600 hover:bg-emerald-50" 
                                  : "bg-navy hover:bg-navy/90 text-white"
                                }
                              >
                                <Upload className="w-4 h-4 mr-1" />
                                {isMultiple ? 'Ajouter' : (hasVideo ? 'Changer' : 'Importer')}
                              </Button>
                            </div>
                          </div>
                          
                          {/* Video list for multiple uploads */}
                          {isMultiple && hasVideo && Array.isArray(videoData) && (
                            <div className="mt-3 pt-3 border-t border-emerald-200 space-y-2">
                              {videoData.map((file, index) => (
                                <div key={index} className="flex items-center justify-between p-2 bg-white rounded-lg">
                                  <div className="flex items-center gap-2">
                                    <Video className="w-4 h-4 text-emerald-600" />
                                    <span className="text-sm text-navy truncate max-w-[200px]">{file.name}</span>
                                  </div>
                                  <button
                                    onClick={() => removeVideo(video.id, index)}
                                    className="text-red-500 hover:text-red-700 p-1"
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Documents Tab */}
              {activeMediaTab === 'documents' && (
                <div className="space-y-4 animate-fade-in">
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <p className="text-sm text-blue-800 flex items-start gap-2">
                      <FileText className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>
                        <strong>Documents utiles :</strong> Le DPE, la taxe foncière et les plans permettent une analyse plus précise 
                        de la rentabilité et des travaux à prévoir.
                        <strong className="text-blue-700"> Ajoutez autant de documents que nécessaire !</strong>
                      </span>
                    </p>
                  </div>
                  
                  <div className="space-y-3">
                    {DOCUMENT_TYPES.map((doc) => {
                      const docData = formData.documents[doc.id];
                      const isMultiple = doc.multiple;
                      const hasDoc = isMultiple 
                        ? (Array.isArray(docData) && docData.length > 0) 
                        : !!docData;
                      const docCount = isMultiple && Array.isArray(docData) ? docData.length : (hasDoc ? 1 : 0);
                      
                      return (
                        <div
                          key={doc.id}
                          className={`p-4 rounded-xl border-2 transition-all ${
                            hasDoc ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-white hover:border-navy/30'
                          }`}
                        >
                          <div className="flex items-center gap-4">
                            <div className={`p-2 rounded-lg ${hasDoc ? 'bg-blue-500 text-white' : 'bg-gray-100 text-navy'}`}>
                              {hasDoc ? <CheckCircle className="w-5 h-5" /> : <doc.icon className="w-5 h-5" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-navy flex items-center gap-2">
                                {doc.label}
                                {isMultiple && <Badge className="bg-gold/20 text-gold text-xs">Illimité</Badge>}
                              </h4>
                              <p className="text-xs text-muted-foreground truncate">{doc.description}</p>
                              {hasDoc && (
                                <p className="text-xs text-blue-600 font-medium mt-1">
                                  {isMultiple ? `${docCount} document(s) ajouté(s)` : (docData?.name || 'Document ajouté')}
                                </p>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <input
                                ref={(el) => fileInputRefs.current[`doc_${doc.id}`] = el}
                                type="file"
                                accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                                multiple={isMultiple}
                                onChange={(e) => handleDocumentSelect(doc.id, e.target.files, isMultiple)}
                                className="hidden"
                              />
                              <Button
                                data-testid={`doc-upload-${doc.id}`}
                                variant={hasDoc ? "outline" : "default"}
                                size="sm"
                                onClick={() => triggerFileInput(`doc_${doc.id}`)}
                                className={hasDoc 
                                  ? "border-blue-500 text-blue-600 hover:bg-blue-50" 
                                  : "bg-navy hover:bg-navy/90 text-white"
                                }
                              >
                                <Upload className="w-4 h-4 mr-1" />
                                {isMultiple ? 'Ajouter' : (hasDoc ? 'Changer' : 'Importer')}
                              </Button>
                            </div>
                          </div>
                          
                          {/* Document list for multiple uploads */}
                          {isMultiple && hasDoc && Array.isArray(docData) && (
                            <div className="mt-3 pt-3 border-t border-blue-200 space-y-2">
                              {docData.map((file, index) => (
                                <div key={index} className="flex items-center justify-between p-2 bg-white rounded-lg">
                                  <div className="flex items-center gap-2">
                                    <FileText className="w-4 h-4 text-blue-600" />
                                    <span className="text-sm text-navy truncate max-w-[200px]">{file.name}</span>
                                  </div>
                                  <button
                                    onClick={() => removeDocument(doc.id, index)}
                                    className="text-red-500 hover:text-red-700 p-1"
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Photos Tab */}
              {activeMediaTab === 'photos' && (
                <div className="space-y-4 animate-fade-in">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="text-sm text-amber-800 flex items-start gap-2">
                      <Image className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <span>
                        <strong>Photos du bien :</strong> Prenez des photos de la façade, des pièces principales et des éventuels défauts visibles.
                        Vous pouvez ajouter plusieurs photos par catégorie.
                      </span>
                    </p>
                  </div>
                  
                  <div className="space-y-3">
                    {PHOTO_TYPES.map((photo) => {
                      const photos = formData.photos[photo.id] || [];
                      const hasPhotos = photos.length > 0;
                      return (
                        <div
                          key={photo.id}
                          className={`p-4 rounded-xl border-2 transition-all ${
                            hasPhotos ? 'border-amber-500 bg-amber-50' : 'border-gray-200 bg-white hover:border-navy/30'
                          }`}
                        >
                          <div className="flex items-center gap-4">
                            <div className={`p-2 rounded-lg ${hasPhotos ? 'bg-amber-500 text-white' : 'bg-gray-100 text-navy'}`}>
                              {hasPhotos ? <CheckCircle className="w-5 h-5" /> : <photo.icon className="w-5 h-5" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-navy">{photo.label}</h4>
                              <p className="text-xs text-muted-foreground truncate">{photo.description}</p>
                              {hasPhotos && (
                                <p className="text-xs text-amber-600 font-medium mt-1">
                                  {photos.length} photo(s) ajoutée(s)
                                </p>
                              )}
                            </div>
                            <div className="flex gap-2">
                              <input
                                ref={(el) => fileInputRefs.current[`photo_${photo.id}`] = el}
                                type="file"
                                accept="image/*"
                                multiple
                                onChange={(e) => handlePhotoSelect(photo.id, e.target.files)}
                                className="hidden"
                              />
                              <Button
                                data-testid={`photo-upload-${photo.id}`}
                                variant={hasPhotos ? "outline" : "default"}
                                size="sm"
                                onClick={() => triggerFileInput(`photo_${photo.id}`)}
                                className={hasPhotos 
                                  ? "border-amber-500 text-amber-600 hover:bg-amber-50" 
                                  : "bg-navy hover:bg-navy/90 text-white"
                                }
                              >
                                <Upload className="w-4 h-4 mr-1" />
                                {hasPhotos ? 'Ajouter' : 'Importer'}
                              </Button>
                            </div>
                          </div>
                          
                          {/* Photo thumbnails */}
                          {hasPhotos && (
                            <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-amber-200">
                              {photos.map((file, index) => (
                                <div key={index} className="relative group">
                                  <div className="w-16 h-16 rounded-lg overflow-hidden bg-gray-100">
                                    <img 
                                      src={URL.createObjectURL(file)} 
                                      alt={`Photo ${index + 1}`}
                                      className="w-full h-full object-cover"
                                    />
                                  </div>
                                  <button
                                    onClick={() => removePhoto(photo.id, index)}
                                    className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    ×
                                  </button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              <p className="text-sm text-center text-muted-foreground pt-4">
                Tous les fichiers sont <span className="font-medium">optionnels mais recommandés</span> pour une analyse optimale
              </p>
              </div>{/* end relative wrapper */}
            </div>
          )}

          {/* Step 5: Partner Free Submission OR Regular Subscription Choice */}
          {step === 5 && partnerSubdomain ? (
            /* ── PARTNER PATH : Audit gratuit ── */
            partnerSubmitSuccess ? (
              /* ── ÉCRAN DE SUCCÈS PARTENAIRE ── */
              <div className="flex flex-col items-center justify-center py-12 px-4 text-center space-y-6">
                {partnerInfo?.logo_url && (
                  <img src={partnerInfo.logo_url} alt={partnerInfo.name} className="h-16 object-contain mb-2" />
                )}
                <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-emerald-100">
                  <CheckCircle className="w-14 h-14 text-emerald-600" />
                </div>
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold text-navy">Demande envoyée !</h2>
                  <p className="text-lg text-emerald-700 font-semibold mt-2">Analyse en cours par notre expert...</p>
                </div>
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 max-w-md w-full">
                  <p className="text-navy/80 text-sm leading-relaxed">
                    Votre dossier a bien été transmis à <strong>{partnerInfo?.name || 'votre partenaire'}</strong>.<br />
                    Un expert va étudier votre projet et vous contactera sous 24–48h par email ou téléphone.
                  </p>
                </div>
                <p className="text-xs text-muted-foreground">Vous pouvez fermer cette page en toute sécurité.</p>
              </div>
            ) : (
            <div className="space-y-6">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-emerald-100 mb-4">
                  <CheckCircle className="w-8 h-8 text-emerald-600" />
                </div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-navy">Votre audit est gratuit !</h2>
                <p className="text-muted-foreground mt-2 max-w-lg mx-auto">
                  {partnerInfo?.name || 'Votre partenaire'} prend en charge votre audit. Complétez vos coordonnées pour recevoir l'analyse.
                </p>
              </div>

              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-emerald-800">Aucun paiement requis</p>
                  <p className="text-sm text-emerald-700">Votre dossier sera traité directement par {partnerInfo?.name || 'l\'équipe partenaire'} qui vous contactera.</p>
                </div>
              </div>

              {isAuthenticated ? (
                <Card className="border-2 border-emerald-200 shadow-lg">
                  <CardContent className="p-6 text-center">
                    <p className="text-navy font-medium">Connecté en tant que <strong>{authUser?.email}</strong></p>
                    <p className="text-sm text-muted-foreground mt-2">Cliquez sur "Envoyer ma demande" pour transmettre votre dossier gratuitement.</p>
                    <button
                      onClick={() => { logout(); }}
                      className="mt-3 text-xs text-muted-foreground underline hover:text-navy transition-colors"
                    >
                      Ce n'est pas moi — Changer de compte
                    </button>
                  </CardContent>
                </Card>
              ) : (
                <Card className="border-2 border-navy/10 shadow-lg">
                  <CardContent className="p-6 space-y-4">
                    <h3 className="font-semibold text-navy text-lg">Vos coordonnées</h3>

                    <div>
                      <Label className="text-navy font-medium text-sm">Prénom / Nom <span className="text-muted-foreground font-normal">(optionnel)</span></Label>
                      <input
                        data-testid="partner-name-input"
                        type="text"
                        value={formData.email_name || ''}
                        onChange={(e) => setFormData({ ...formData, email_name: e.target.value })}
                        placeholder="Jean Dupont"
                        className="w-full mt-1 h-12 px-3 border-2 border-gray-200 rounded-lg focus:border-amber-400 focus:outline-none"
                      />
                    </div>

                    <div>
                      <Label className="text-navy font-medium text-sm">Email <span className="text-red-500">*</span></Label>
                      <input
                        data-testid="partner-email-input"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="votre@email.com"
                        className="w-full mt-1 h-12 px-3 border-2 border-gray-200 rounded-lg focus:border-amber-400 focus:outline-none"
                      />
                    </div>

                    <div>
                      <Label className="text-navy font-medium text-sm">Téléphone <span className="text-red-500">*</span></Label>
                      <input
                        data-testid="partner-phone-input"
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="06 12 34 56 78"
                        className="w-full mt-1 h-12 px-3 border-2 border-gray-200 rounded-lg focus:border-amber-400 focus:outline-none"
                      />
                      <p className="text-xs text-muted-foreground mt-1">Requis pour que le partenaire puisse vous recontacter</p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
            )
          ) : step === 5 ? (
            /* ── REGULAR PATH : Choix de forfait Stripe ── */
            <div className="space-y-6">
              <div className="text-center mb-8">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gold/10 mb-4">
                  <Award className="w-8 h-8 text-gold" />
                </div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-navy">Choisissez votre accompagnement</h2>
                <p className="text-muted-foreground mt-2 max-w-lg mx-auto">
                  L'application ne vend pas un simple PDF. Vous achetez l'expertise d'un <span className="text-gold font-semibold">Marchand de Biens avec 25 ans d'expérience</span> dans le bâtiment.
                </p>
              </div>

              {/* Packages Grid */}
              <div className="grid md:grid-cols-3 gap-4">
                {PACKAGES.map((pkg) => {
                  const isSelected = selectedPackage === pkg.id;
                  return (
                    <button
                      key={pkg.id}
                      data-testid={`package-${pkg.id}`}
                      onClick={() => setSelectedPackage(pkg.id)}
                      className={`relative p-6 rounded-2xl border-2 text-left transition-all ${
                        isSelected ? pkg.selectedColor : pkg.color
                      } hover:shadow-lg`}
                    >
                      {pkg.popular && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                          <Badge className="bg-gold text-black font-semibold">
                            <Star className="w-3 h-3 mr-1" />
                            Plus populaire
                          </Badge>
                        </div>
                      )}
                      {pkg.badge && (
                        <div className="absolute -top-3 right-4">
                          <Badge className="bg-navy text-white font-semibold">{pkg.badge}</Badge>
                        </div>
                      )}
                      
                      <div className="mb-4">
                        <h3 className="font-heading text-xl font-bold text-navy">{pkg.name}</h3>
                        <p className="text-sm text-muted-foreground">{pkg.subtitle}</p>
                      </div>
                      
                      <div className="mb-4">
                        <span className="text-3xl font-bold text-navy">{pkg.price}</span>
                        <span className="text-muted-foreground ml-1">{pkg.priceLabel}</span>
                        {pkg.savings && (
                          <Badge className="ml-2 bg-emerald-100 text-emerald-700 text-xs">{pkg.savings}</Badge>
                        )}
                      </div>
                      
                      <p className="text-sm text-muted-foreground mb-4">{pkg.description}</p>
                      
                      <ul className="space-y-2">
                        {pkg.features.map((feature, idx) => (
                          <li key={idx} className="flex items-center gap-2 text-sm">
                            <CheckCircle className={`w-4 h-4 ${isSelected ? 'text-gold' : 'text-emerald-500'}`} />
                            <span className="text-navy">{feature}</span>
                          </li>
                        ))}
                      </ul>
                      
                      {isSelected && (
                        <div className="absolute top-4 right-4">
                          <div className="w-6 h-6 rounded-full bg-gold flex items-center justify-center">
                            <CheckCircle className="w-4 h-4 text-white" />
                          </div>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* Expert Signature - Compact */}
              <div className="bg-gradient-to-r from-navy/5 to-gold/5 border border-navy/10 rounded-xl p-4 mt-6">
                <p className="text-sm italic text-navy/80 leading-relaxed text-center">
                  "Ne laissez pas un vice caché ou une erreur de chiffrage briser votre rentabilité. Investissez avec l'assurance d'un ancien du bâtiment."
                </p>
                <p className="text-xs text-gold font-semibold mt-2 text-center">— L'Expert, 25 ans de métier</p>
              </div>

              {selectedPackage && (
                <div className="text-center pt-4">
                  <p className="text-sm text-muted-foreground">
                    Vous avez sélectionné : <span className="font-semibold text-navy">{PACKAGES.find(p => p.id === selectedPackage)?.name}</span>
                  </p>
                </div>
              )}

              {/* Stripe Security Badges */}
              <div className="flex flex-wrap items-center justify-center gap-4 mt-6 pt-4 border-t border-gray-200">
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
                  <Shield className="w-4 h-4 text-emerald-600" />
                  <span className="text-xs font-medium text-gray-700">Paiement 100% Sécurisé</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
                  <svg className="w-12 h-5" viewBox="0 0 60 25" fill="none">
                    <path d="M5 12.5C5 8.91 7.91 6 11.5 6H48.5C52.09 6 55 8.91 55 12.5C55 16.09 52.09 19 48.5 19H11.5C7.91 19 5 16.09 5 12.5Z" fill="#635BFF"/>
                    <text x="15" y="15" fill="white" fontSize="8" fontWeight="bold">stripe</text>
                  </svg>
                </div>
                <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
                  <CheckCircle className="w-4 h-4 text-emerald-600" />
                  <span className="text-xs font-medium text-gray-700">Satisfait ou Remboursé</span>
                </div>
              </div>
            </div>
          ) : null}
            </>
          )}

        </div>

        {/* Navigation Buttons - Only for Audit Mode */}
        {mode === 'audit' && !partnerSubmitSuccess && (
        <div className="flex items-center justify-between flex-wrap gap-2 mt-8 pt-6 border-t">
          {step > 1 ? (
            <Button
              data-testid="prev-step-btn"
              variant="outline"
              onClick={() => setStep(step - 1)}
              className="border-navy text-navy hover:bg-navy hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Précédent
            </Button>
          ) : (
            <div />
          )}
          
          {step < totalSteps ? (
            <Button
              data-testid="next-step-btn"
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
              className={`font-semibold px-8 shadow-lg transition-all ${
                canProceed() 
                  ? 'bg-gold hover:bg-gold/90 text-black' 
                  : 'bg-gold/40 text-black/50 cursor-not-allowed'
              }`}
            >
              Suivant
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              data-testid="submit-project-btn"
              onClick={handleSubmit}
              disabled={loading || !canProceed()}
              className={`font-semibold px-8 shadow-lg ${canProceed() ? 'bg-gold hover:bg-gold/90 text-black animate-pulse' : 'bg-gold/40 text-black/50 cursor-not-allowed'}`}
            >
              {loading ? (
                <>Envoi en cours...</>
              ) : freeAuditToken ? (
                <>
                  <Gift className="w-4 h-4 mr-2" />
                  Envoyer le dossier (gratuit)
                </>
              ) : partnerSubdomain ? (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Envoyer ma demande gratuitement
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Analyser mon projet
                </>
              )}
            </Button>
          )}
        </div>
        )}
      </main>

      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md animate-fade-in shadow-2xl">
            <CardContent className="p-6">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gold mb-3">
                  <Euro className="w-6 h-6 text-black" />
                </div>
                <h3 className="font-heading text-xl font-bold text-navy">
                  Paiement sécurisé
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Votre compte sera créé automatiquement après paiement
                </p>
              </div>
              
              <div className="space-y-4">
                <div className="bg-gold/10 border border-gold/30 rounded-lg p-4 text-center">
                  <p className="text-sm text-navy">
                    Forfait sélectionné : <span className="font-bold">{PACKAGES.find(p => p.id === selectedPackage)?.name}</span>
                  </p>
                  <p className="text-2xl font-bold text-gold mt-1">
                    {PACKAGES.find(p => p.id === selectedPackage)?.price}€ TTC
                  </p>
                </div>
                
                <div>
                  <Label htmlFor="email">Votre email</Label>
                  <Input
                    id="email"
                    data-testid="email-input"
                    type="email"
                    placeholder="votre@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-12 mt-2 border-2 focus:border-gold"
                  />
                </div>

                <div>
                  <Label htmlFor="phone-modal">Téléphone <span className="text-red-500">*</span></Label>
                  <Input
                    id="phone-modal"
                    data-testid="phone-modal-input"
                    type="tel"
                    placeholder="06 12 34 56 78"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    className="h-12 mt-2 border-2 focus:border-gold"
                  />
                </div>

                <div>
                  <Label htmlFor="password">Créez votre mot de passe</Label>
                  <Input
                    id="password"
                    data-testid="password-input"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-12 mt-2 border-2 focus:border-gold"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Minimum 6 caractères
                  </p>
                </div>

                <div>
                  <Label htmlFor="confirmPassword">Confirmez le mot de passe</Label>
                  <Input
                    id="confirmPassword"
                    data-testid="confirm-password-input"
                    type="password"
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="h-12 mt-2 border-2 focus:border-gold"
                  />
                </div>
                
                <Button
                  data-testid="pay-now-btn"
                  onClick={handleDirectPayment}
                  disabled={loading || !email || !phone || phone.trim().length < 8 || !password || password.length < 6 || !confirmPassword || password !== confirmPassword}
                  className="w-full h-14 bg-gold hover:bg-gold/90 text-black font-bold text-lg"
                >
                  {loading ? 'Redirection...' : '💳 Payer maintenant'}
                </Button>
                
                <p className="text-xs text-center text-muted-foreground">
                  Vous serez automatiquement connecté à votre espace après paiement
                </p>
                
                <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
                  <svg viewBox="0 0 40 16" className="h-4 w-auto">
                    <rect fill="#635BFF" width="40" height="16" rx="3"/>
                    <text x="8" y="11" fill="white" fontSize="8" fontWeight="bold">stripe</text>
                  </svg>
                  <span>Paiement 100% sécurisé</span>
                </div>
              </div>
              
              <button
                onClick={() => setShowEmailModal(false)}
                className="w-full mt-3 text-sm text-muted-foreground hover:text-navy"
              >
                Annuler
              </button>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal fonctionnalité partenaire (Pièces du Dossier + Stratégies avancées) */}
      <Dialog open={showPartnerLockedModal} onOpenChange={setShowPartnerLockedModal}>
        <DialogContent className="max-w-md text-center">
          <div className="flex flex-col items-center gap-4 py-2">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-amber-100">
              <Shield className="w-7 h-7 text-amber-600" />
            </div>
            <h2 className="text-xl font-bold text-navy">Fonctionnalité premium</h2>
            <p className="text-muted-foreground text-sm leading-relaxed">
              L'envoi de médias et les stratégies avancées font partie de l'<strong>Audit Personnalisé complet</strong>.
            </p>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 w-full text-left">
              <p className="text-sm text-amber-800 font-semibold mb-1">Contactez votre agence</p>
              <p className="text-sm text-amber-700">
                Votre conseiller vous accompagnera pour aller plus loin dans votre projet immobilier.
              </p>
            </div>
            <button
              onClick={() => setShowPartnerLockedModal(false)}
              className="w-full py-3 rounded-xl bg-navy text-white font-semibold hover:bg-navy/90 transition-colors"
            >
              Compris, continuer
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
